import { useCallback, useEffect, useState } from 'react';

export type Lang = 'en' | 'ar';
export type Dir = 'ltr' | 'rtl';

export const I18N: Record<Lang, Record<string, string>> = {
  en: {
    dashboard: 'Dashboard',
    accounting: 'Accounting',
    subscriptions: 'Subscriptions',
    expenses: 'Expenses',
    payroll: 'Payroll',
    employees: 'Employees',
    contracts: 'Contracts',
    invoices: 'Invoices',
    quotations: 'Quotations',
    bills: 'Bills',
    payments: 'Payments',
    bank: 'Bank',
    customers: 'Customers',
    vendors: 'Vendors',
    chart_of_accounts: 'Chart of Accounts',
    journal: 'Journal',
    reports: 'Reports',
    users: 'Users & Roles',
    settings: 'Settings',
    finance: 'Finance',
    people: 'People',
    sales: 'Sales',
    system: 'System',
    recurring: 'Recurring',
    welcome: 'Welcome back',
    search: 'Search anything…',
    new_: 'New',
    export_: 'Export',
    print: 'Print',
    revenue: 'Revenue',
    net_profit: 'Net profit',
    burn: 'Monthly burn',
    cash: 'Cash on hand',
    name: 'Name',
    email: 'Email',
    role: 'Role',
    status: 'Status',
    amount: 'Amount',
    date: 'Date',
    actions: 'Actions',
    add: 'Add',
    edit: 'Edit',
    delete: 'Delete',
    save: 'Save',
    cancel: 'Cancel',
    view: 'View',
    active: 'Active',
    paused: 'Paused',
    overdue: 'Overdue',
    draft: 'Draft',
    sent: 'Sent',
    signed: 'Signed',
    paid: 'Paid',
    pending: 'Pending',
    this_week: 'Week',
    this_month: 'Month',
    this_year: 'Year',
    revenue_vs_expenses: 'Revenue vs Expenses',
    vs_last: 'vs last month',
    quick_actions: 'Quick actions',
    recent: 'Recent activity',
    bhd: 'BHD',
    usd: 'USD',
    collapse_sidebar: 'Collapse',
    expand_sidebar: 'Expand',
    sign_in: 'Sign in',
    sign_in_lede: 'Use your company credentials. New here? Ask your admin to invite you.',
    password: 'Password',
    keep_signed_in: 'Keep me signed in',
    forgot: 'Forgot?',
    continue_: 'Continue',
    invalid_credentials: 'Invalid email or password.',
    internal_system: 'Internal system — access restricted to Join Future personnel.',
    coming_soon: 'Coming soon',
    coming_soon_sub: 'This module is part of a later build unit and is not available yet.',
    back_to_dashboard: 'Back to dashboard',
    not_found: 'Page not found',
    not_found_sub: 'The route you tried to open does not exist.',
    forbidden: 'Forbidden',
    forbidden_sub: 'Your role does not have access to this page.',
    logout: 'Sign out',
    notifications: 'Notifications',
    loading: 'Loading',
    no_data: 'No data yet',
    dashboard_unavailable: 'Dashboard data is not available yet.',

    // shared / table
    search_placeholder: 'Search…',
    show_inactive: 'Show inactive',
    only_active: 'Active only',
    all: 'All',
    yes: 'Yes',
    no: 'No',
    optional: 'optional',
    confirm: 'Confirm',
    saving: 'Saving…',
    creating: 'Creating…',
    deleting: 'Deleting…',
    inactive: 'Inactive',
    restore: 'Restore',
    set_default: 'Set default',
    default_: 'Default',
    none: 'None',
    error_generic: 'Something went wrong.',
    saved: 'Saved.',
    created: 'Created.',
    deleted: 'Deleted.',
    restored: 'Restored.',
    posted: 'Posted.',
    uploaded: 'Uploaded.',
    uploading: 'Uploading…',
    preview_pdf: 'Preview PDF',
    attachments: 'Attachments',
    no_attachments: 'No attachments yet',
    confirm_delete_attachment: 'Delete this file?',

    // customers / vendors
    new_customer: 'New customer',
    edit_customer: 'Edit customer',
    new_vendor: 'New vendor',
    edit_vendor: 'Edit vendor',
    customer_details: 'Customer details',
    vendor_details: 'Vendor details',
    name_ar: 'Name (Arabic)',
    phone: 'Phone',
    vat_number: 'VAT number',
    cr_number: 'CR number',
    address: 'Address',
    notes: 'Notes',
    recent_invoices: 'Recent invoices',
    recent_quotations: 'Recent quotations',
    recent_bills: 'Recent bills',
    recent_expenses: 'Recent expenses',
    no_invoices: 'No invoices yet',
    no_bills: 'No bills yet',
    no_expenses: 'No expenses yet',
    no_quotations: 'No quotations yet',
    confirm_delete_customer: 'Mark this customer as inactive?',
    confirm_delete_vendor: 'Mark this vendor as inactive?',
    confirm_delete_account: 'Delete this account? This cannot be undone.',

    // accounts
    chart_of_accounts_sub: 'All ledger accounts grouped by type.',
    new_account: 'New account',
    edit_account: 'Edit account',
    code: 'Code',
    type: 'Type',
    normal_balance: 'Normal balance',
    parent: 'Parent',
    debit: 'Debit',
    credit: 'Credit',
    asset: 'Assets',
    liability: 'Liabilities',
    equity: 'Equity',
    expense: 'Expenses',
    balance: 'Balance',
    system_account: 'System',

    // bank accounts
    bank_accounts: 'Bank accounts',
    new_bank_account: 'New bank account',
    edit_bank_account: 'Edit bank account',
    opening_balance: 'Opening balance',
    opening_balance_hint: 'Optional. Posts owner capital to the ledger against Owner Equity.',
    bank_name: 'Bank name',
    iban: 'IBAN',
    currency: 'Currency',

    // journal
    journal_viewer: 'Journal viewer',
    post_manual_entry: 'Post manual entry',
    journal_entry: 'Journal entry',
    entry_number: 'Entry #',
    entry_date: 'Entry date',
    memo: 'Memo',
    source: 'Source',
    total: 'Total',
    from_date: 'From',
    to_date: 'To',
    account: 'Account',
    add_line: 'Add line',
    remove: 'Remove',
    debits_total: 'Debits',
    credits_total: 'Credits',
    balanced: 'Balanced',
    out_of_balance: 'Out of balance',
    difference: 'Difference',
    reversed: 'Reversed',
    manual: 'Manual',
    invoice: 'Invoice',
    bill: 'Bill',
    payment: 'Payment',
    payroll_src: 'Payroll',
    subscription: 'Subscription',

    // === Wave 3 Group A ===
    new_invoice: 'New invoice',
    edit_invoice: 'Edit invoice',
    invoice_details: 'Invoice details',
    customer: 'Customer',
    issue_date: 'Issue date',
    due_date: 'Due date',
    bill_date: 'Bill date',
    expires_at: 'Expires at',
    expense_date: 'Expense date',
    vendor: 'Vendor',
    category: 'Category',
    method: 'Method',
    bank_account: 'Bank account',
    bank_account_in: 'Bank account that will receive the payment',
    bank_account_out: 'Bank account that will disburse the payment',
    expense_account: 'Expense account',
    description: 'Description',
    qty: 'Qty',
    rate: 'Rate',
    line_amount: 'Amount',
    subtotal: 'Subtotal',
    vat: 'VAT',
    vat_rate: 'VAT rate',
    vat_amount: 'VAT amount',
    discount: 'Discount',
    grand_total: 'Total',
    amount_paid: 'Paid',
    amount_due: 'Outstanding',
    total_due: 'Total due',
    add_line_item: 'Add line',
    no_lines: 'No line items yet',
    select_customer: 'Select a customer',
    select_vendor: 'Select a vendor',
    select_account: 'Select an account',
    select_bank_account: 'Select a bank account',

    // Advance payment
    advance_section: 'Advance payment',
    advance_type: 'Bill an advance',
    advance_none: 'None — bill the full amount',
    advance_type_percent: 'Percent of net',
    advance_type_amount: 'Fixed amount',
    advance_percent_value: 'Advance %',
    advance_amount_value: 'Advance amount',
    advance_percent_hint: '0–100 (e.g. 50 for 50%)',
    advance_amount_hint: 'At most subtotal − discount',
    advance_custom_label_en: 'Line label (English)',
    advance_custom_label_ar: 'Line label (Arabic)',
    advance_default_label: 'Advance Payment',
    advance_balance_later: 'Balance to be invoiced later',
    advance_badge: 'Advance',
    advance_value_required: 'Enter the advance value',
    advance_invalid: 'The advance rounds to zero — raise it or bill the full amount',
    advance_percent_invalid: 'Advance cannot exceed 100%',
    advance_exceeds_net: 'Advance cannot exceed subtotal minus discount',
    advance_type_required: 'Select an advance type',

    new_quotation: 'New quotation',
    edit_quotation: 'Edit quotation',
    quotation_details: 'Quotation details',

    new_bill: 'New bill',
    edit_bill: 'Edit bill',
    bill_details: 'Bill details',

    new_expense: 'New expense',
    edit_expense: 'Edit expense',
    expense_details: 'Expense details',

    // Action labels
    send_: 'Send',
    sign_: 'Sign',
    convert_to_invoice: 'Convert to invoice',
    record_payment: 'Record payment',
    void_: 'Void',
    download_pdf: 'Download PDF',
    submit_: 'Submit',
    approve_: 'Approve',
    reject_: 'Reject',
    post_: 'Post',
    sent_ok: 'Sent.',
    signed_ok: 'Signed.',
    rejected_ok: 'Rejected.',
    voided_ok: 'Voided.',
    payment_recorded: 'Payment recorded.',
    submitted_ok: 'Submitted.',
    approved_ok: 'Approved.',
    converted_ok: 'Converted to invoice.',

    // Statuses
    status_draft: 'Draft',
    status_sent: 'Sent',
    status_signed: 'Signed',
    status_expired: 'Expired',
    status_rejected: 'Rejected',
    status_partial: 'Partial',
    status_paid: 'Paid',
    status_overdue: 'Overdue',
    status_void: 'Void',
    status_open: 'Open',
    status_submitted: 'Submitted',
    status_approved: 'Approved',

    // Filters
    filter_status: 'Status',
    all_statuses: 'All statuses',

    // Pagination
    prev: 'Previous',
    next: 'Next',
    page: 'Page',
    of: 'of',

    // Inline validation
    discount_exceeds_subtotal: 'Discount cannot exceed subtotal',
    invalid_url: 'Enter a valid URL (https://…)',

    // Confirms
    confirm_void_invoice: 'Void this invoice? Its journal entry will be reversed.',
    confirm_void_bill: 'Void this bill? Its journal entry will be reversed.',
    confirm_delete_invoice: 'Delete this draft invoice?',
    confirm_delete_quotation: 'Delete this draft quotation?',
    confirm_delete_bill: 'Delete this draft bill?',
    confirm_delete_expense: 'Delete this draft expense?',
    confirm_reject_quotation: 'Reject this quotation?',
    confirm_reject_expense: 'Reject this expense?',

    // Modal labels
    payment_amount: 'Amount',
    paid_at: 'Paid at',
    payment_method: 'Method',
    payment_reference: 'Reference',
    payment_memo: 'Memo',
    method_bank: 'Bank',
    method_cash: 'Cash',
    method_card: 'Card',
    method_wire: 'Wire',
    payments_history: 'Payments',
    no_payments: 'No payments recorded',
    journal_summary: 'Journal entry',
    no_journal_entry: 'No journal entry posted yet',

    // Misc
    converted_invoice: 'Converted invoice',
    convert: 'Convert',
    notes_optional: 'Notes (optional)',
    line_subtotal: 'Subtotal',
    line_total_due: 'Outstanding',
    invoice_summary: 'Summary',
    quotation_summary: 'Summary',
    bill_summary: 'Summary',
    expense_summary: 'Summary',
    submitted_by: 'Submitted by',
    approved_by: 'Approved by',
    receipt_url: 'Receipt URL',
    save_draft: 'Save draft',

    // === Wave 3 Group B ===
    // employees
    new_employee: 'New employee',
    edit_employee: 'Edit employee',
    employee_details: 'Employee details',
    employee_number: 'Employee #',
    department: 'Department',
    contract_type: 'Contract type',
    base_salary: 'Base salary',
    joined_at: 'Joined at',
    terminated_at: 'Terminated at',
    full_time: 'Full-time',
    part_time: 'Part-time',
    contractor: 'Contractor',
    terminated: 'Terminated',
    terminate: 'Terminate',
    confirm_terminate_employee:
      'Terminate this employee? Any linked user account will be deactivated and all sessions revoked. This cannot be undone.',
    recent_payslips: 'Recent payslips',
    no_payslips: 'No payslips yet',
    period: 'Period',
    gross: 'Gross',
    deductions: 'Deductions',
    net_amount: 'Net',
    linked_user: 'Linked user',
    no_linked_user: 'No linked user',
    // payroll
    payroll_runs: 'Payroll runs',
    new_payroll_run: 'New payroll run',
    payroll_run: 'Payroll run',
    payroll_run_details: 'Payroll run details',
    period_start: 'Period start',
    period_end: 'Period end',
    pay_date: 'Pay date',
    total_gross: 'Total gross',
    total_deductions: 'Total deductions',
    total_net: 'Total net',
    payslips: 'Payslips',
    employee: 'Employee',
    payment_status: 'Payment status',
    post_run: 'Post run',
    pay_run: 'Pay run',
    delete_run: 'Delete run',
    confirm_delete_payroll_run: 'Delete this draft payroll run?',
    confirm_post_payroll_run:
      'Post this payroll run? Each payslip will be ledger-posted and cannot be deleted afterwards.',
    pick_bank_account: 'Pick a bank account',
    pay_payroll_title: 'Pay payroll run',
    optional_employees: 'Specific employees (optional)',
    optional_employees_hint: 'Leave empty to include all active employees.',
    no_payslips_in_run: 'No payslips in this run',
    // subscriptions
    new_subscription: 'New subscription',
    edit_subscription: 'Edit subscription',
    subscription_details: 'Subscription details',
    service: 'Service',
    cost: 'Cost',
    cycle: 'Cycle',
    monthly: 'Monthly',
    quarterly: 'Quarterly',
    yearly: 'Yearly',
    auto_charge: 'Auto-charge',
    auto: 'Auto',
    manual_charge: 'Manual',
    pick_vendor: 'Pick a vendor',
    pick_expense_account: 'Pick an expense account',
    owner: 'Owner',
    pick_owner_employee: 'Pick an owner employee',
    pause: 'Pause',
    resume: 'Resume',
    charge_now: 'Charge now',
    recent_charges: 'Recent charges',
    no_charges: 'No charges yet',
    charged_at: 'Charged at',
    cancelled: 'Cancelled',
    confirm_cancel_subscription:
      'Cancel this subscription? It will stop generating new charges.',
    confirm_charge_now:
      'Generate a vendor bill for this subscription now? The next charge date will advance.',
    confirm_delete_subscription: 'Delete this subscription?',
    // contracts
    new_contract: 'New contract',
    edit_contract: 'Edit contract',
    contract_details: 'Contract details',
    party: 'Party',
    party_name: 'Party name',
    kind: 'Kind',
    contract_value: 'Value',
    ends_at: 'Ends at',
    body: 'Body',
    body_md: 'Body (English Markdown)',
    body_md_ar: 'Body (Arabic Markdown)',
    service_agreement: 'Service agreement',
    saas_subscription: 'SaaS subscription',
    employment: 'Employment',
    nda: 'NDA',
    mou: 'MOU',
    other: 'Other',
    expired: 'Expired',
    sign: 'Sign',
    signed_at: 'Signed at',
    confirm_sign_contract: 'Mark this contract as signed?',
    confirm_terminate_contract: 'Terminate this contract? This cannot be undone.',
    confirm_delete_contract: 'Delete this draft contract?',
    // contract e-signature & templates
    esign_employer: 'Employer signature',
    esign_employee: 'Employee signature',
    esign_sign_here: 'Sign here',
    esign_clear: 'Clear',
    esign_submit: 'Submit signature',
    esign_name_placeholder: 'Full name',
    esign_signed_by: 'Signed by',
    esign_signed_on: 'Signed on',
    esign_awaiting: 'Awaiting signature',
    esign_saved: 'Signature saved.',
    contract_templates: 'Contract Templates',
    new_template: 'New Template',
    load_template: 'Load Template',
    template_name: 'Template name',
    template_name_ar: 'Template name (Arabic)',
    select_template: 'Select a template…',
    it_employment_template: 'IT Employment (Part-time)',
    linked_employee: 'Linked employee',
    select_employee: 'Select an employee',
    cpr: 'CPR / National ID',
    employee_contracts: 'Contracts',
    no_contracts: 'No contracts yet',
    pdf_preview: 'Preview PDF',

    // === Wave 3 Group C ===
    // Settings
    settings_company: 'Company',
    settings_tax: 'Tax',
    settings_branding: 'Branding',
    settings_subtitle: 'Company profile, tax defaults, and document branding.',
    company_name: 'Company name',
    company_name_ar: 'Company name (Arabic)',
    default_currency: 'Default currency',
    language: 'Language',
    default_vat_rate: 'Default VAT rate (%)',
    fy_start_month: 'Fiscal year starts',
    invoice_terms: 'Invoice terms & conditions',
    bank_iban: 'Bank IBAN',
    payment_details: 'Payment details',
    document_bank_account: 'Bank account on documents',
    document_bank_account_custom: 'Custom',
    document_bank_account_hint: 'This is the account customers are asked to pay into on their invoices.',
    document_bank_account_empty: 'No bank accounts yet — add one first in',
    settings_saved: 'Settings saved.',
    month_jan: 'January',
    month_feb: 'February',
    month_mar: 'March',
    month_apr: 'April',
    month_may: 'May',
    month_jun: 'June',
    month_jul: 'July',
    month_aug: 'August',
    month_sep: 'September',
    month_oct: 'October',
    month_nov: 'November',
    month_dec: 'December',

    // Users
    users_subtitle: 'Manage who can sign in and what they can do.',
    invite_heading: 'Join {org} on Railit',
    invite_body: 'You were invited as {role}. This link belongs to {email}.',
    invite_existing_account: 'You already have a Railit account. Accepting adds this company to it, and you switch between them from the top of the screen.',
    invite_accept: 'Accept and open the workspace',
    invite_invalid_title: 'This invitation is no longer valid',
    invite_invalid_body: 'It may have expired, been used already, or been withdrawn. Ask whoever invited you to send a new one.',
    wa_country: 'Country',
    wa_country_code: 'Country code',
    notes_empty: 'No notes on this record yet.',
    notes_placeholder: 'Something a colleague reading this in six months would want to know.',
    note_add: 'Add note',
    note_by_ai: 'AI',
    note_asked_for_by: 'asked for by',
    note_written_by: 'by',
    wa_other_country: 'Other country',
    wa_whose_phone: 'Whose phone',
    wa_my_own_phone: 'My own phone',
    wa_number_local: 'Phone number',
    wa_will_link: 'Will link',
    wa_my_numbers: 'My numbers',
    wa_my_numbers_lede: 'Add your own phone and message the assistant from WhatsApp. Several numbers are free. It answers about this company, and asks which one if you keep more than one set of books.',
    wa_assistant_number: 'Message the assistant on',
    wa_add_number: 'Add my number',
    wa_add_number_hint: 'Send it a message from that phone afterwards. The first message is what proves the number is yours.',
    wa_number_added: 'Number added. Message the assistant from it to finish.',
    wa_verified: 'Verified',
    wa_awaiting_first_message: 'Waiting for your first message',
    wa_no_numbers: 'No numbers yet.',
    wa_no_usage_yet: 'Nothing spent from this number yet.',
    wa_credits_30d: 'credits, 30 days',
    wa_turns_30d: 'conversations',
    wa_last_used: 'last used',
    add_user: 'Add user',
    send_invitation: 'Send invitation',
    invite_link_hint: 'The invitation is on its way. If the email does not arrive, send them this link instead:',
    new_user: 'New user',
    edit_user: 'Edit user',
    reset_password: 'Reset password',
    disable_user: 'Disable user',
    confirm_disable_user: 'Disable this user? They will no longer be able to sign in.',
    user_disabled: 'User disabled.',
    password_reset_ok: 'Password updated.',
    user_created: 'User created.',
    user_updated: 'User updated.',
    role_owner: 'Owner',
    role_admin: 'Admin',
    role_finance: 'Finance',
    role_engineer: 'Engineer',
    role_sales: 'Sales',
    role_designer: 'Designer',
    role_viewer: 'Viewer',
    mfa: 'MFA',
    mfa_enabled: 'MFA on',
    mfa_disabled: 'MFA off',
    last_login_at: 'Last login',
    new_password: 'New password',
    password_min_8: 'At least 8 characters, including a letter and a number.',
    never_signed_in: 'Never',

    // Dashboard (extra)
    revenue_mtd: 'Revenue MTD',
    profit_mtd: 'Profit MTD',
    monthly_burn: 'Monthly burn',
    cash_on_hand: 'Cash on hand',
    recent_activity: 'Recent activity',
    upcoming: 'Upcoming',
    overdue_invoices: 'Overdue invoices',
    subs_due_7d: 'Subscriptions due in 7 days',
    no_recent_activity: 'No recent activity',
    no_overdue_invoices: 'No overdue invoices',
    no_upcoming_subs: 'No subscriptions due this week',
    next_charge: 'Next charge',
    today: 'Today',
    custom: 'Custom',
    income_and_expense: 'Income & Expense',
    top_expenses: 'Top Expenses',
    all_expenses: 'All Expenses',
    total_receivables: 'Total Receivables',
    total_payables: 'Total Payables',
    current: 'Current',
    cash_flow: 'Cash Flow',
    incoming: 'Incoming',
    outgoing: 'Outgoing',
    net_cash: 'Net Cash',
    profit: 'Profit',

    // Reports
    reports_subtitle: 'Financial statements, AR ageing, and quick PDF exports.',
    report_pnl: 'Profit & Loss',
    report_balance_sheet: 'Balance Sheet',
    report_cashflow: 'Cash flow',
    report_ar_aging: 'AR Aging',
    report_pnl_sub: 'Revenue, expenses and net profit for the selected period.',
    report_balance_sheet_sub: 'Assets, liabilities and equity at a point in time.',
    report_cashflow_sub: 'Cash inflows and outflows over the selected period.',
    report_ar_aging_sub: 'Outstanding receivables grouped by age.',
    quick_pdfs: 'Quick PDFs',
    weekly_pdf: 'Weekly report (PDF)',
    daily_pdf: 'Daily report (PDF)',
    weekly_pdf_sub: 'Generate this week’s management summary.',
    daily_pdf_sub: 'Generate today’s daily snapshot.',
    download: 'Download',
    pdf_downloaded: 'PDF downloaded.',
    week_number: 'Week number',
    year: 'Year',
    prepared_by: 'Prepared by',
    pnl_revenue: 'Revenue',
    pnl_expenses: 'Expenses',
    pnl_net: 'Net profit',
    bs_assets: 'Assets',
    bs_liabilities: 'Liabilities',
    bs_equity: 'Equity',
    cf_opening: 'Opening balance',
    cf_inflows: 'Inflows',
    cf_outflows: 'Outflows',
    cf_closing: 'Closing balance',
    cf_total_in: 'Total inflows',
    cf_total_out: 'Total outflows',
    aging_as_of: 'As of',
    no_invoices_in_bucket: 'No invoices in this bucket',
    as_of: 'As of',

    // Settings: Claude / MCP
    settings_mcp: 'Claude / MCP',
    mcp_title: 'Claude / MCP integration',
    mcp_status_connected: 'Connected',
    mcp_status_not_installed: 'Not installed',
    mcp_tools: 'tools',
    mcp_last_sync: 'Last sync',
    mcp_api_base: 'API base',
    mcp_install_path: 'Server path',
    mcp_capabilities_title: 'What Claude can do',
    mcp_cap_upload_docs: 'Upload documents',
    mcp_cap_send_pdf: 'Render & send PDFs',
    mcp_cap_full_crud: 'Full CRUD on every entity',
    mcp_cap_audit: 'Every action audit-logged',
    mcp_tool_groups: 'Tool groups',
    mcp_setup_title: 'How to connect Claude Desktop',
    mcp_config_path: 'Claude Desktop config file',
    mcp_config_snippet: 'Config snippet (paste into the file above)',
    mcp_copy: 'Copy',
    mcp_copied: 'copied to clipboard',
    mcp_os_mac: 'macOS',
    mcp_os_win: 'Windows',
    mcp_os_linux: 'Linux',
    mcp_step_1: 'Clone or copy the repo to your local machine.',
    mcp_step_3: 'Open the Claude Desktop config file at the path above (create it if it does not exist).',
    mcp_step_4: 'Generate an API token above, then paste it into the snippet as JOINFUTURE_API_TOKEN (replacing jf_PASTE_TOKEN_HERE).',
    mcp_step_5: 'Restart Claude Desktop. The joinfuture tools will appear in the tools panel.',
    mcp_examples_title: 'Example prompts you can give Claude',
    mcp_security_note: 'The config file stores your API token in plaintext on this machine. Anyone with access to this file can act as you in the ERP. Keep your machine locked, do not commit the config to git, and revoke the token from this page to disconnect.',
    mcp_api_tokens: 'API Tokens',
    mcp_token_name: 'Token name',
    mcp_generate: 'Generate',
    mcp_copy_once: 'Copy now — it will not be shown again.',
    mcp_token: 'Token',
    mcp_no_tokens: 'No tokens yet. Generate one to connect Claude.',
    mcp_revoke: 'Revoke',
    mcp_token_never_used: 'never used',
    mcp_token_last_used: 'last used',
    mcp_token_revoked: 'revoked',
    mcp_connect_any_ai: 'Connect to any AI',
    mcp_connect_any_ai_sub: 'One ERP, every assistant. Pick a platform — Claude.ai, ChatGPT, Gemini, Cursor — and copy the snippet.',
    mcp_remote_endpoint: 'Remote endpoint',
    mcp_auth: 'auth',
    mcp_setup_badge: '(setup)',
    mcp_json_config: 'JSON config',
    net_income_trend: 'Net income trend',
    access_denied: 'Access denied',
    recent_subscriptions: 'Recent subscriptions',
    no_subscriptions: 'No subscriptions',
    enable_user: 'Enable user',
    user_enabled: 'User enabled.',
    subs_mtd: 'MTD',
    live_cost_from: 'Live from',
    live_cost_updated: 'updated',
    live_cost_integration: 'integration',

    // ===== Railit SaaS =====
    crm: 'Customers & sales',
    org_settings: 'Organization settings',
    settings_organization: 'Organization',
    settings_billing: 'Billing',
    settings_whatsapp: 'WhatsApp',

    // auth
    create_account: 'Create your account',
    signup_lede: 'Your workspace is ready in under a minute. 10 days free, no card needed.',
    signup_failed: 'Could not create the account. Try again.',
    company_name_required: 'Enter your company name',
    your_name: 'Your name',
    name_required: 'Enter your name',
    work_email: 'Work email',
    email_invalid: 'Enter a valid email',
    password_min: 'At least 8 characters',
    start_free_trial: 'Start free trial',
    have_account: 'Already have an account?',
    no_account: "Don't have an account?",
    trial: 'Trial',
    trial_days_n: '{n} days free',
    no_card_needed: 'No card needed',
    from_price: 'From',
    per_month: 'month',
    price_extra_user: '+ $4 per extra user',

    // modules
    mod_finance: 'Finance',
    mod_finance_sub: 'Invoices, bills, VAT, ledger',
    mod_crm: 'CRM',
    mod_crm_sub: 'Customers, quotes, contracts',
    mod_hr: 'HR',
    mod_hr_sub: 'Employees, payroll, payslips',
    mod_inventory: 'Inventory',
    mod_inventory_sub: 'Items, stock, movements',
    mod_pos: 'POS',
    mod_pos_sub: 'Counter sales into the ledger',
    mod_ai: 'AI agents',
    mod_ai_sub: 'Chat, MCP, WhatsApp',

    // billing
    billing_base: 'Plan',
    billing_extra_users: 'Extra users ({n})',
    billing_monthly_total: 'Monthly total',
    billing_renews_on: 'Renews on {date}',
    billing_manage: 'Manage billing',
    billing_subscribe: 'Subscribe',
    billing_addons: 'Add-ons',
    billing_seats: 'Seats',
    billing_seats_detail: '{used} paid user(s). The first {included} is included in the plan; each extra one is billed monthly.',
    billing_seats_short: '{used} paid user(s) · {price} / month',
    agent_session_cost: 'Credits used in this conversation',
    agent_turn_cost: '{n} credits',
    agent_attach: 'Attach',
    agent_voice: 'Voice',
    agent_stop: 'Stop',
    agent: 'Agent',
    agent_new: 'New chat',
    agent_history: 'History',
    agent_wide: 'Widen',
    agent_narrow: 'Narrow',
    agent_send: 'Send',
    agent_greeting: 'How can I help?',
    agent_scope: 'Acting in {org}',
    agent_placeholder: 'Ask anything…',
    agent_not_wired: 'The agent is not connected yet.',
    agent_failed: 'Something went wrong. Try again.',
    agent_no_credits: 'This workspace is out of AI credits. Top up in Billing.',
    agent_stopped_budget: 'Stopped — that task was longer than one request allows.',
    agent_stopped_credits: 'Stopped early to leave you some credits.',
    agent_files: 'Files',
    agent_resize: 'Resize the panel',
    agent_expand_full: 'Open in Agent mode',
    agent_snap_release: 'Release to open Agent mode',
    agent_snap_confirm: 'Press Enter to open Agent mode',
    agent_doc_loading: 'Rendering…',
    agent_doc_download: 'Download',
    agent_doc_failed: 'Could not open this document',
    agent_file_unread: 'not read',
    agent_file_too_big: '{name} is larger than 10 MB.',
    agent_remove_file: 'Remove',
    agent_reading: 'Reading your files…',
    agent_thinking: 'Working…',
    agent_proposed: 'Proposed change',
    agent_apply: 'Apply',
    agent_discard: 'Discard',
    agent_applied: 'Applied',
    agent_discarded: 'Discarded',
    agent_apply_failed: 'Could not apply that. It may have expired.',
    agent_not_applied: 'Nothing has been saved yet.',
    agent_steps: '{n} steps',
    agent_steps_one: '1 step',
    agent_steps_failed: 'one failed',
    agent_no_history: 'No conversations yet.',
    agent_untitled: 'Untitled',
    agent_when_today: 'Today',
    agent_when_yesterday: 'Yesterday',
    agent_when_week: 'Last 7 days',
    agent_when_older: 'Older',
    vs_last_month: 'vs. same period last month',
    recurring_commitments: 'Recurring commitments',
    across_accounts: 'Across all accounts',
    pct_overdue: '{n}% of this is already overdue',
    view_all: 'View all',
    pdf_failed: 'Could not build the PDF. Try again.',
    agent_threads: 'Conversations',
    agent_greeting_hint: 'Ask about your ledger, or attach a file to work from.',
    agent_mode: 'Agent mode',
    agent_mode_on: 'Agent',
    agent_mode_off: 'Manual',
    agent_mode_hint: 'Turn the whole workspace into a conversation.',
    model_auto: 'Auto',
    model_auto_hint: 'Picks the right level for each question — cheap on a lookup, careful on a reconciliation.',
    model_pick_hint: 'How carefully should the agent work?',
    model_per_month: '~{n} questions a month',
    model_credits_each: '{n} credits each',
    model_thinking: 'Think',
    model_thinking_hint: 'Reason step by step before answering. Slower, and about twice the credits.',
    model_thinking_cost: 'About {n} credits a question with thinking on.',
    thinking_live: 'Thinking…',
    thinking_done: 'Thought about this',
    memory: 'Memory',
    memory_title: 'What the agent remembers',
    memory_subtitle: 'Facts it keeps between conversations, so you do not have to repeat them. Delete anything that is wrong or out of date.',
    memory_company: 'About your company',
    memory_company_hint: 'everyone in the workspace sees these',
    memory_personal: 'About you',
    memory_personal_hint: 'only you see these',
    memory_by_you: 'you wrote this',
    memory_by_agent: 'the agent noted this',
    memory_used: 'used in {n} conversations',
    memory_unused: 'not used yet',
    memory_active: 'Active',
    memory_export: 'Export as Markdown',
    memory_empty: 'Nothing remembered yet',
    memory_empty_hint: 'Tell the agent something like "we always invoice on 45-day terms" and it will keep it.',
    memory_deleted: 'Forgotten.',
    memory_failed: 'Could not update that memory.',
    memory_upload: 'Upload .md',
    memory_uploaded: 'Added {n} files.',
    memory_kind_fact: 'fact',
    memory_kind_note: 'note',
    memory_kind_skill: 'how-to',
    memory_kind_fact_hint: 'Short enough that the agent carries it in every conversation.',
    memory_kind_note_hint: 'A document. The agent sees only the description below, and opens it when that looks relevant.',
    memory_kind_skill_hint: 'A procedure. The agent reads it when it is about to do this thing.',
    memory_total_cost: 'All of this adds {tok} tokens to every question — about {cr} credits a turn.',
    memory_carried: 'carried in every conversation · {tok} tokens',
    memory_opened: 'opened {n} times',
    memory_never_opened: 'never opened — has cost {cr} credits',
    memory_cost_hint: 'Adds {tok} tokens to every question. {cr} credits so far.',
    memory_when_used: 'The agent opens this when',
    memory_file: 'The file',
    memory_words: '{n} words — opened only when the description above matches what is being asked',
    billing_credits_left: 'credits left',
    billing_credits_top_up: 'Running low — top up',
    billing_credits_trial: 'left in your trial',
    billing_credits_from_topup: 'left, from your top-up',
    account_avatar_hint: 'Click or drop a PNG, JPEG or WebP. Up to 2 MB.',
    billing_team: 'Team size',
    billing_team_lede: 'Buy seats ahead of filling them — a seat you already paid for costs nothing to fill.',
    billing_seats_using: '{n} in the workspace now',
    billing_seats_included_then: '{n} included, then {price} each',
    billing_seats_ahead: 'Buying {n} seat(s) ahead.',
    billing_seats_fewer: 'Fewer seats',
    billing_seats_more: 'More seats',
    billing_then_from_second: '{pct}% off. From the second month it is {price}.',
    purchases: 'Purchases',
    money: 'Money',
    workspace: 'Workspace',
    doc_accent_from_logo: 'Pick from logo',
    doc_accent_pick_failed: 'Could not read a colour from that logo.',
    per_month_short: ' /mo',
    billing_billed_yearly: '{total} billed yearly',
    billing_billed_yearly_total: '{total} charged once a year',
    billing_delta_month: 'a month',
    billing_switch_to_yearly: 'Switching to yearly billing',
    billing_switch_to_monthly: 'Switching to monthly billing',
    done: 'Done',
    error: 'Something went wrong.',
    billing: 'Billing',
    billing_configure_lede: 'Build your subscription on the left. The bill on the right is exactly what you will be charged.',
    billing_plan: 'Plan',
    billing_plan_lede: 'Everything else is priced against this.',
    billing_popular: 'Most chosen',
    billing_months_free: '{n} months free',
    billing_credits_monthly: '{n} AI credits a month',
    billing_first_month_is: 'first month {price}',
    billing_ai: 'AI credits',
    billing_ai_monthly_lede: 'Your monthly allowance. It resets every period and does not carry over.',
    billing_included: 'Included',
    billing_included_in_plan: 'Comes with the plan',
    billing_this_month: 'Used this period',
    billing_ai_model_note: 'Runs on {model}',
    billing_purchased_left: '{n} bought credits left',
    billing_addons_lede: 'Products you can switch on and off.',
    billing_addon_brings: '{messages} messages a month. AI runs on your plan allowance.',
    billing_addon_needs_plan: 'Available on {plan} and above',
    billing_topup: 'Credit packs',
    billing_topup_lede: 'For the month the allowance runs out before the month does.',
    billing_topup_separate: 'Bought once, never expires, and spent only after the monthly allowance is gone. Not part of the subscription below.',
    billing_approx_exchanges: 'about {n} exchanges',
    billing_percent_off: '{n}% off',
    billing_your_bill: 'Your bill',
    billing_bill_lede_month: 'Charged every month. Change or cancel any time.',
    billing_bill_lede_year: 'Charged once a year. Ten months billed, two free.',
    billing_seats_note: '{used} users, {included} included in the plan',
    billing_total_month: 'Per month',
    billing_total_year: 'Per year',
    billing_first_month: 'Your first month',
    billing_then_renews: '{pct}% off. Renews at {price} after that.',
    billing_checkout: 'Continue to checkout',
    billing_update: 'Update subscription',
    billing_no_changes: 'No changes to apply',
    billing_updated: 'Your subscription was updated.',
    billing_owner_only: 'Only the Owner can change billing.',
    billing_trial_free: 'Free for {days} days. No card yet.',
    billing_trial_running: 'Your trial is free until {date}. A card is only needed to continue after that.',
    billing_renews_plain: 'Renews on {date}',
    billing_confirm_change: 'Confirm the change',
    billing_confirm_change_lede: 'Stripe prorates the difference against what you have already paid for this period.',
    billing_due_now: 'Charged today',
    billing_due_now_unknown: 'Calculated at checkout',
    billing_next_invoice: 'Next invoice',
    billing_confirm: 'Confirm',
    billing_confirming: 'Confirming with Stripe…',
    billing_confirming_lede: 'This takes a second. Your payment went through.',
    billing_thanks: 'Thank you',
    billing_thanks_named: 'Thank you, {name}',
    billing_thanks_lede: 'Your workspace is on {plan}. Here is what was agreed.',
    billing_thanks_pending: 'Your payment is with Stripe. The subscription will appear here within a minute.',
    billing_billing_starts: 'Billing starts',
    billing_topup_done: 'Credits added',
    billing_topup_done_lede: '{n} credits are on your account. They never expire.',
    company_settings: 'Company settings',
    account_settings: 'Your account',
    account_settings_lede:
      'Your name, your password and the companies you belong to. These follow you everywhere — company settings do not.',
    account_profile: 'Profile',
    account_profile_lede: 'How you appear to every team you work with.',
    account_title: 'Title',
    account_email_fixed: 'Your sign-in address. Contact support to change it.',
    account_companies: 'Your companies',
    account_companies_lede: 'One account, as many companies as you need. Your role can differ in each.',
    account_current: 'Open',
    account_open: 'Switch to',
    account_security: 'Security',
    account_security_lede: 'Your password protects every company you belong to.',
    account_current_password: 'Current password',
    account_new_password: 'New password',
    account_change_password: 'Change password',
    account_password_scope:
      'At least 8 characters. Changing it signs you out everywhere else.',
    account_password_changed: 'Password changed.',
    account_password_changed_sessions: 'Password changed. {n} other session(s) signed out.',
    account_google_only:
      'This account signs in with Google, so there is no Railit password to change. Manage it in your Google account.',
    plan_trialing_on: 'Trialing',
    plan_subscribe_to: 'Subscribe',
    billing_leaving_checkout: 'Taking you to secure checkout…',
    billing_leaving_topup: 'Taking you to secure checkout…',
    billing_leaving_portal: 'Opening your billing portal…',
    seat_limit_trial:
      'Your plan includes {included} user(s). Subscribe to add more — every extra user is {price} a month.',
    seat_limit_billing: 'Activate the subscription before adding users.',
    seat_next_costs: 'Next user: {price} / month',
    view_plans: 'View plans',
    billing_stripe_off: 'Billing is not configured on this deployment.',
    billing_action_needed: 'Billing needs attention',
    topup_title: 'Add AI credits',
    topup_subtitle: 'One payment. The credits never expire.',
    topup_credits_word: 'credits',
    topup_addition:
      'These {pack} are added to the {included} your plan already includes, so this month you have {total}.',
    topup_saving: '{percent}% cheaper per credit than the smallest pack.',
    topup_cta: 'Pay {price}',
    topup_leaving: 'Opening checkout…',
    topup_note: 'Card details are entered on Stripe. We never see them.',
    credits_low_badge: 'Credits running low',
    credits_out_badge: 'Out of AI credits',
    credits_out_chat_title: 'You are out of AI credits',
    credits_out_chat_body:
      'Add credits to keep the conversation going. Everything else in the workspace still works.',
    credits_out_chat_cta: 'Add credits',
    trial_offer_badge: 'Activate now: 12 extra days free',
    trial_offer_short: '12 more free if you activate',
    trial_last_day: 'Last day of your trial',
    trial_ending_title: 'Your trial ends in {n} days',
    trial_ending_body: 'Activate now and you get 12 more days free before the first charge, then 55% off your first month. Nothing stops working today.',
    trial_ending_cta: 'Choose a plan',
    hold_title: 'This workspace is on hold',
    hold_body:
      'It has been dormant since the subscription lapsed. Nothing was deleted. Choose a plan to bring it back, plus a one-off {fee} to restore and reopen the company.',
    hold_cta: 'Restore this company',
    hold_note: 'Your books, documents and history are all still here, exactly as they were.',
    hold_warning:
      'If this stays unpaid, the workspace goes on hold on {date}. It is not deleted: reopening it later costs a plan plus a one-off restoration fee.',
    blocked_badge: 'Read-only',
    blocked_payment_title: 'Your workspace has stopped accepting entries',
    blocked_payment_body:
      'The subscription payment did not go through, and the retry window has passed. Updating the card turns writing back on immediately.',
    blocked_payment_cta: 'Update payment method',
    blocked_trial_title: 'Your trial has ended',
    blocked_trial_body:
      'Choose a plan to start creating again. Everything you entered during the trial is still here, exactly as you left it.',
    blocked_trial_cta: 'Choose a plan',
    blocked_readonly_note:
      'Nothing has been deleted. You can still sign in, read every document and export your books.',
    blocked_keep_reading: 'Keep reading my books',
    grace_banner_one: 'Payment failed. Writing stops tomorrow.',
    grace_banner_days: 'Payment failed. {days} days left to update your card.',
    grace_banner_fix: 'Fix it',
    billing_reason_trial_expired: 'Your trial has ended. Subscribe to keep making changes.',
    billing_reason_payment_overdue: 'A payment failed and the grace period is over.',
    billing_reason_payment_retrying: 'A payment failed — we are retrying it.',
    billing_reason_subscription_inactive: 'This subscription is not active.',
    billing_reason_organization_suspended: 'This organization is suspended.',
    sub_status_trialing: 'Trial',
    sub_status_active: 'Active',
    sub_status_past_due: 'Past due',
    sub_status_unpaid: 'Unpaid',
    sub_status_canceled: 'Cancelled',
    sub_status_incomplete: 'Incomplete',
    sub_status_paused: 'Paused',
    trial_days_left: '{n} days left on trial',
    trial_billing_starts: 'Billing starts {date}',

    // The plan worked out from a trial's real usage — services/planRecommendation.ts.
    // Nobody picked a tier at sign-up, so this is where the question gets
    // answered, and it has to read as arithmetic rather than as an offer.
    rec_title: 'The plan your usage points to',
    rec_lede: 'Worked out from what this workspace actually did — not from a guess on the day you signed up.',
    rec_days_left: '{n} day(s) left',
    rec_measured: 'Measured over {days} day(s)',
    rec_measured_seats: '{n} user(s) in the workspace',
    rec_measured_credits: '{used} AI credits spent — about {projected} a month at that rate',
    rec_measured_credits_zero: 'No AI credits spent',
    rec_answer: '{plan} with {seats} user(s)',
    rec_apply: 'Use {plan}',
    rec_applied: 'Set to {plan}. Check the bill, then continue when you are ready.',
    rec_choose_other:
      'Pick any other plan below and it costs you nothing to change your mind — this is a suggestion, not a gate.',
    rec_reason_used_ai: 'You used the AI, so Starter is out: it has no AI credits at all.',
    rec_reason_too_early:
      'No AI used yet, but {days} day(s) is too short to call that a preference — this keeps it switched on.',
    rec_reason_no_ai: 'No AI credits spent in {days} day(s).',
    rec_starter_tradeoff:
      'Starter drops the assistant, the WhatsApp agent and anything else that spends credits. Your books, your documents and the MCP server all carry on.',
    rec_reason_used_whatsapp: 'WhatsApp is connected, so the add-on is on this quote.',
    rec_reason_credit_burn: 'At about {projected} credits a month, the tier below would not last the month.',
    rec_reason_credits_topped_up:
      'The plan alone gives {included} credits a month, so a monthly credit tier is included here to reach {total}.',
    rec_reason_credits_short:
      'Nothing we sell covers {projected} credits a month. This is the largest allowance at {total} — expect to buy a credit pack in a heavy month.',
    rec_reason_extra_seats: '{n} users: {included} in the plan, {extra} more at {price} each.',
    rec_reason_seats_included: 'All {n} of your users are included in the plan.',
    rec_expired_title: 'Your trial has ended',
    rec_expired_lede:
      'Nothing was deleted. Everything you entered is still here to read and export. New entries and the AI stay paused until you choose a plan.',

    // appearance
    theme: 'Theme',
    theme_light: 'Light',
    theme_dark: 'Dark',
    theme_system: 'System',
    addon_credits: '{n} messages / month',
    addon_usage: '{used} of {quota} messages used this month',
    enable: 'Enable',
    disable: 'Disable',
    on: 'On',
    off: 'Off',

    // organization
    org_products: 'Products',
    org_products_hint: 'Everything is included in the plan. Switch off what you do not need and it disappears from the sidebar.',
    org_members: 'People',
    org_people_moved: 'People and roles are managed in Users.',
    org_people_moved_action: 'Open Users',
    org_seat_pricing_hint: 'Viewers are free. Every other role costs $4 (BHD 1.5) per month.',
    invite: 'Invite',
    invite_sent: 'Invitation sent',
    pending_invitations: 'Pending invitations',
    no_pending_invitations: 'No pending invitations',
    revoke: 'Revoke',
    invite_person: 'Invite someone',
    expires: 'Expires',
    seat: 'Seat',
    billable: 'Paid',
    free: 'Free',

    // whatsapp
    whatsapp_upsell: 'The WhatsApp assistant answers questions about your books, sends payment reminders and pushes a daily report — $11 (BHD 4) per month.',
    whatsapp_enable_addon: 'Enable the add-on',
    whatsapp_number: 'Business number',
    whatsapp_connect_hint: 'Paste the credentials from your Meta WhatsApp Business account.',
    whatsapp_connected: 'WhatsApp connected',
    whatsapp_connected_as: 'Connected as {phone}',
    whatsapp_phone: 'Phone number (E.164)',
    whatsapp_phone_id: 'Phone number ID',
    whatsapp_token: 'Access token',
    whatsapp_contacts: 'Allowed contacts',
    whatsapp_contacts_hint: 'A number can only read company data once it is linked to a Railit user.',
    whatsapp_no_contacts: 'No one has messaged the number yet.',
    whatsapp_daily_report: 'Daily report',
    whatsapp_daily_report_hint: 'A short digest of what was invoiced, collected, spent and what is overdue.',
    not_linked: 'Not linked',
    connect: 'Connect',
    recipients: 'Recipients',
    time: 'Time',
    last_sent: 'Last sent',

    // ===== Billing cards =====
    ai_credits: 'AI credits this month',
    ai_credits_hint: 'Priced from real {model} usage — about {n} assistant exchanges.',
    feat_ai_credits: '{n} AI credits a month',
    billing_synced: 'Subscription synced from Stripe',
    sub_active: 'Subscribed',
    sub_confirmed: 'Your subscription is active',
    sub_trial_then: 'Free until {date}, then billed monthly. Cancel any time.',
    sub_card_on_file: 'Billed monthly. Cancel any time from the billing portal.',
    billing_ends_on: 'Ends on {date}',
    billing_already_paid: 'Already paid? Sync from Stripe',
    set_group_company: 'Company',
    set_group_workspace: 'Workspace',
    set_group_billing: 'Billing',
    set_group_integrations: 'Integrations',
    set_desc_company: 'Legal name, CR, VAT number, address',
    set_desc_tax: 'Default VAT rate, currency, fiscal year',
    set_desc_branding: 'Logo and colours on your documents',
    set_desc_org: 'Company profile and the products you use',
    set_desc_users: 'People, roles, invitations and seats',
    set_desc_billing: 'Plan, seats, add-ons and AI credits',
    set_desc_mcp: 'Connect Claude or ChatGPT to your books',
    set_desc_whatsapp: 'Your business number and daily reports',
    // ===== Multi-company =====
    addon_mcp: 'AI & MCP',
    addon_mcp_d: 'Connect Claude or ChatGPT to this workspace.',
    your_companies: 'Your companies',
    new_company: 'New company',
    create_company: 'Create company',
    new_company_hint: 'A separate workspace with its own data, team and subscription. You will be its Owner.',
    new_company_trial: 'Starts on a 14-day free trial. No card needed.',
    org_created: '{name} is ready',
    country: 'Country',
    country_bh: 'Bahrain',
    country_sa: 'Saudi Arabia',
    country_ae: 'United Arab Emirates',
    country_kw: 'Kuwait',
    country_qa: 'Qatar',
    country_om: 'Oman',
    seats_line_note: '{n} paid user(s) · {price} each past the first',
    module_always_on: 'Always on — the rest is built on it',
    current_plan: 'Current',
    addon_email: 'Email',
    plan_sub: 'Everything Railit does, for the whole company.',
    plan_includes_seats: 'Includes {n} user',
    per_user_month: '/ user / month',
    seats_sub: 'What each person past the first costs.',
    seats_in_use: '{n} paid user(s) right now',
    seats_included: 'Included in the plan',
    seats_extra: 'Extra users',
    seats_cost: 'Added to the bill',
    manage_people: 'Manage people',
    owner_only: 'Only the Owner can change billing.',
    included_in_plan: 'Included in the plan',
    trial_no_card: 'no card on file yet',
    email_unlimited: 'No sending quota',
    addon_used: 'Used this month',
    addon_remaining: 'Remaining',
    addon_sub_whatsapp: 'Your own WhatsApp number, answering from the ledger.',
    addon_sub_email: 'Invoices, quotes, reminders and reports, sent from Railit.',
    feat_all_products: 'ERP, Finance, CRM, HR, Inventory, POS, AI, Forms',
    feat_viewers_free: 'Viewers are free — read-only costs nothing',
    feat_mcp: 'MCP server for Claude and ChatGPT',
    feat_email: 'Unlimited email sending',
    feat_seat_prorated: 'Added and removed mid-month, prorated',
    feat_wa_digest: 'Daily or weekly digest at a time you pick',
    feat_wa_reminders: 'Payment reminders that follow the invoice',
    feat_wa_arabic: 'Answers in Arabic or English',
    feat_email_docs: 'Attached to the document it belongs to',
    feat_email_log: 'Every message logged and retryable',

    /* ───────────────────────────────────────────────────────────────────────
       Phase 2B · People & partners — customers, vendors, employees, payroll,
       subscriptions, contracts. Append-only block; keep en/ar in sync.
       ─────────────────────────────────────────────────────────────────────── */
    customers_subtitle: 'The companies and people you invoice.',
    vendors_subtitle: 'The suppliers you buy from and pay.',
    employees_subtitle: 'Your team, their pay and their contracts.',
    payroll_subtitle: 'Monthly runs, payslips and payouts.',
    subscriptions_subtitle: 'Recurring services and what they cost you.',
    contracts_subtitle: 'Agreements, signatures and renewal dates.',
    all_kinds: 'All kinds',
    all_years: 'All years',
    clear_filters: 'Clear filters',
    no_results: 'Nothing matches those filters',
    no_results_hint: 'Try a different search, or clear the filters.',
    no_customers_yet: 'No customers yet',
    no_customers_hint: 'Add the first company you invoice and it will show up here.',
    no_vendors_yet: 'No vendors yet',
    no_vendors_hint: 'Add a supplier to start recording bills and expenses against it.',
    no_employees_yet: 'No employees yet',
    no_employees_hint: 'Add your first team member to run payroll and issue contracts.',
    no_payroll_runs_yet: 'No payroll runs yet',
    no_payroll_runs_hint: 'Create a run to generate payslips for the period you choose.',
    no_subscriptions_yet: 'No subscriptions yet',
    no_subscriptions_hint: 'Track a recurring service to see its cost and next charge date.',
    no_contracts_yet: 'No contracts yet',
    no_contracts_hint: 'Draft a contract, sign it electronically and keep the PDF here.',
    basic_info: 'Basics',
    contact_details: 'Contact',
    tax_registration: 'Tax & registration',
    job_details: 'Job',
    compensation: 'Compensation',
    billing_schedule: 'Billing schedule',
    posting_accounts: 'Posting',
    commercial_terms: 'Value & term',
    contract_body: 'Contract body',
    esign_title: 'Electronic signatures',
    esign_subtitle: 'Both parties sign here; the PDF carries the signatures.',
    template_hint: 'Start from a saved template, then edit the text below.',
    /* `posted` is already taken by the toast copy ("Posted."); a run's *state*
       needs a bare noun for chips and the lifecycle strip. */
    run_status_posted: 'Posted',
    run_stage_draft: 'Amounts can still be edited.',
    run_stage_posted: 'Posted to the ledger, waiting for payout.',
    run_stage_paid: 'Paid out from the bank account.',
    pending_edits_hint: 'Finish editing deductions before posting.',
    employee_count: 'Employees',
    back: 'Back',

    // ===== Phase 2A · document pages (invoices, quotations, bills, expenses, payments) =====
    line_items: 'Line items',
    more_actions: 'More actions',
    filter_search: 'Search',
    invoices_sub: 'What you have billed, and what is still owed to you.',
    quotations_sub: 'Offers out with customers, and what became of them.',
    bills_sub: 'What suppliers have billed you, and what is still due.',
    expenses_sub: 'Out-of-pocket and card spend, from claim to posting.',
    payments_sub: 'Every payment in and out, and the document it settled.',
    direction: 'Direction',
    inbound: 'Inbound',
    outbound: 'Outbound',
    all_directions: 'All directions',
    inbound_payment: 'Inbound payment',
    outbound_payment: 'Outbound payment',
    linked_to: 'Linked to',
    no_linked_doc: 'No linked document',
    reference: 'Reference',
    payslip: 'Payslip',
    created_at: 'Created',
    no_payments_sub: 'Pay an invoice, a bill or a payroll run and it will show up here.',
    total_inbound: 'Total in',
    total_outbound: 'Total out',
    net_flow: 'Net',
    /* ───────────────────────────────────────────────────────────────────────
       Phase 2C · reports, accounting & the shared widgets. Append-only block;
       keep en/ar in sync.
       ─────────────────────────────────────────────────────────────────────── */
    stmt_total_revenue: 'Total revenue',
    stmt_total_expenses: 'Total expenses',
    stmt_total_assets: 'Total assets',
    stmt_total_liabilities: 'Total liabilities',
    stmt_total_equity: 'Total equity',
    stmt_liabilities_and_equity: 'Liabilities and equity',
    stmt_net_margin: 'net margin',
    stmt_net_movement: 'Net movement',
    stmt_no_revenue: 'No revenue in this period',
    stmt_no_expenses: 'No expenses in this period',
    stmt_no_movement: 'No movement in this period',
    stmt_no_balances: 'No accounts with a balance',
    stmt_invoice_count: 'invoices',
    aging_by_bucket: 'Outstanding by age',
    aging_total_outstanding: 'Total outstanding',
    aging_nothing_outstanding: 'Nothing outstanding',
    aging_all_settled: 'Every invoice due on or before this date has been settled.',
    days_overdue: 'days overdue',
    coa_no_accounts: 'No accounts of this type yet',
    coa_expand: 'Expand',
    coa_collapse: 'Collapse',
    journal_sub: 'Every posted entry, and what created it.',
    bank_accounts_sub: 'Where the cash sits, and what each account holds.',
    upload: 'Upload',
    preview: 'Preview',
    preview_unavailable: 'Preview is not available for this file type.',
    close: 'Close',
    aws_live_cost: 'AWS month-to-date',
    aws_not_configured: 'AWS billing keys not configured',
    edit_signature: 'Edit signature',

    /* ===== APPENDED — dashboard bands + post-checkout confirmation ===== */
    /* Band headings on the dashboard home. */
    outstanding: 'Outstanding',
    performance: 'Performance',
    activity: 'Activity',
    /* Coming back from Stripe Checkout. Nothing here is hardcoded: the plan,
       the price, the interval and the dates all come off the live payload. */
    checkout_confirming: 'Confirming your subscription…',
    checkout_confirming_sub: 'Stripe is sending the details across. It only takes a moment.',
    checkout_done_title: "You're on {plan}",
    checkout_trial_line: 'Free until {date} — then {price} per {interval}.',
    checkout_renew_line: '{price} per {interval}. Next charge {date}.',
    checkout_charged_line: '{price} per {interval}, starting today.',
    checkout_first_month: 'Your first month is {first}, then {price} per month.',
    checkout_included: '{seats} users included · {credits} AI credits a month',
    checkout_cancel_note:
      'Cancel any time from this page — you keep everything until the end of the period you have paid for.',
    checkout_ack: 'Got it',
    checkout_pending_title: 'Payment received',
    checkout_pending_body:
      'Stripe has your payment. Your plan will switch over within a minute — reload this page if it has not.',
    interval_month: 'month',
    interval_year: 'year',

    /* ===== APPENDED — document design (Settings → Branding) =====
       Everything a tenant can change about how its PDFs look. Append-only
       block; keep it byte-for-byte parallel with the Arabic one below. */
    doc_design: 'Document design',
    doc_design_sub: 'How the PDFs your customers receive look. Changes apply to every document from now on.',
    doc_brand: 'Brand',
    doc_logo: 'Logo',
    doc_logo_hint: 'PNG, JPEG, WebP or SVG. Up to 2 MB. Printed at the height you choose — never stretched.',
    doc_logo_upload: 'Upload logo',
    doc_logo_replace: 'Replace',
    doc_logo_remove: 'Remove',
    doc_logo_none: 'No logo yet — documents use your company name as a wordmark.',
    doc_logo_size: 'Printed height',
    doc_logo_uploaded: 'Logo updated',
    doc_logo_removed: 'Logo removed',
    doc_accent: 'Accent colour',
    doc_accent_hint: 'Used for the document number, totals and rules.',
    doc_accent_inherit: 'Use workspace brand colour',
    doc_header_style: 'Header treatment',
    doc_header_rule: 'Accent rule',
    doc_header_fill: 'Filled band',
    doc_header_fill_hint: 'A filled header needs a logo that reads on colour.',
    doc_layout: 'Layout',
    doc_layout_classic: 'Classic',
    doc_layout_classic_desc: 'Hairline rules, bordered panels, dark footer.',
    doc_layout_modern: 'Modern',
    doc_layout_modern_desc: 'The accent carries the page. Soft panels, no borders.',
    doc_layout_minimal: 'Minimal',
    doc_layout_minimal_desc: 'No fills. Hairlines, small type, plenty of air.',
    doc_page: 'Page',
    doc_language: 'Document language',
    doc_language_en: 'English',
    doc_language_ar: 'Arabic',
    doc_language_both: 'Both, side by side',
    doc_language_hint: 'Bilingual documents keep every figure left-to-right, so the money columns never move.',
    doc_paper: 'Paper size',
    doc_paper_a4: 'A4',
    doc_paper_letter: 'Letter',
    doc_margin: 'Margins',
    doc_margin_hint: 'Millimetres of white space around the page.',
    doc_contents: 'What appears on the document',
    doc_show_cr: 'CR number',
    doc_show_vat: 'VAT number',
    doc_show_address: 'Address',
    doc_show_phone: 'Phone',
    doc_show_email: 'Email',
    doc_show_website: 'Website',
    doc_website: 'Website address',
    doc_show_iban: 'Bank / IBAN block',
    doc_show_payment_terms: 'Terms & notes block',
    doc_show_signature: 'Signature line',
    doc_show_watermark: 'DRAFT / PAID watermark',
    doc_show_watermark_hint: 'Stamps unfinalized and settled documents diagonally across every page.',
    doc_text: 'Wording',
    doc_terms_en: 'Terms (English)',
    doc_terms_ar: 'Terms (Arabic)',
    doc_terms_hint: 'Printed in the sign-off block. Leave empty for the default wording.',
    doc_footer_note_en: 'Footer note (English)',
    doc_footer_note_ar: 'Footer note (Arabic)',
    doc_footer_note_hint: 'One short line, repeated at the bottom of every page.',
    doc_preview: 'Live preview',
    doc_preview_sub: 'The real template, with sample data.',
    doc_preview_doc: 'Preview document',
    doc_preview_failed: 'Could not render the preview.',
    doc_preview_retry: 'Try again',
    doc_preview_open_pdf: 'Open as PDF',
    doc_reset: 'Reset to defaults',
    doc_reset_confirm: 'Reset the document design?',
    doc_reset_body: 'Every setting on this page goes back to the Railit defaults, and the uploaded logo is deleted. Documents already sent are unaffected.',
    doc_reset_done: 'Document design reset',
    doc_unsaved: 'Unsaved changes',
    doc_invoice: 'Invoice',
    doc_quote: 'Quotation',
    doc_bill: 'Vendor bill',
    doc_payslip: 'Payslip',

    /* ===== APPENDED — billing rebuild: tiers, add-ons, AI credits ===== */
    plans_choose: 'Choose your plan',
    plans_sub: 'Change tier whenever you like — Stripe prorates the difference.',
    plan_current: 'Current plan',
    plan_switch_to: 'Switch to this',
    plan_upgrade_to: 'Upgrade',
    plan_start_trial: 'Start free trial',
    interval_monthly: 'Monthly',
    interval_yearly: 'Yearly',
    interval_yearly_note: '2 months free',
    plan_billed_yearly: '{price} billed once a year',
    plan_seats_included: '{n} users included',
    plan_seat_extra: 'then {price} per extra user',
    plan_ai_credits_month: '{n} AI credits a month',
    plan_no_ai: 'No AI credits — MCP only',
    plan_feat_mcp: 'MCP server for Claude and ChatGPT',
    plan_feat_whatsapp: 'WhatsApp assistant can be added',
    plan_feat_priority: 'Priority support',
    plan_feat_api: 'API tokens for your own scripts',
    offer_headline: '{percent}% off your first month',
    offer_sub: 'Month one is {price} instead of {full}. Card taken at checkout; it renews at {full} after that.',
    offer_trial_first: 'Or try it free for {days} days first — no card, nothing charged.',
    offer_first_month: 'First month {price}',
    offer_badge: 'Save {percent}%',
    addons_title: 'Add-ons',
    addons_sub: 'Extras you can switch on and off at any time. Billed with the subscription.',
    addon_brings: 'Brings {messages} messages and {credits} AI credits a month.',
    addon_locked_on_plan: 'Not sold on {plan}',
    addon_upgrade_to_add: 'Available from {plan} up',
    addon_on: 'Active',
    credits_title: 'AI credits',
    credits_allowance: 'Monthly allowance',
    credits_left: '{left} left of {quota}',
    credits_spent_this_period: '{n} spent this month',
    credits_bought_balance: '{n} bought — never expires',
    credits_exchanges: '≈ {n} assistant exchanges',
    credits_burn: 'At this month\u2019s rate that is about {n} a day.',
    credits_days_cover: 'Enough for roughly {n} more days.',
    credits_runs_out: 'On this month\u2019s rate the allowance runs out before the month does.',
    credits_tier_title: 'Raise the monthly allowance',
    credits_tier_sub: 'A recurring add-on: renews with the subscription and resets every month.',
    credits_tier_included: 'Included with {plan}',
    credits_tier_total: '{n} a month',
    credits_tier_choose: 'Choose',
    credits_tier_chosen: 'Selected',
    credits_topup: 'Buy a one-off top-up',
    credits_topup_sub: 'Paid once. Lands immediately, never expires, and is only spent after the monthly allowance is gone.',
    credits_pack_credits: '{n} credits',
    credits_buy: 'Buy',
    credits_no_ai_plan: 'This plan has no AI budget — the assistant, WhatsApp and the agents are off.',
    credits_no_ai_cta: 'Upgrade to {plan}',
    topup_done_title: 'Credits added',
    topup_done_body: '{n} AI credits are on your balance. They never expire, and they are spent only once the monthly allowance is gone.',
    topup_pending_body: 'Stripe has your payment. The credits land within a minute — reload this page if they have not.',
    plan_change_title: 'Switch to {plan}',
    plan_change_new_price: 'New price',
    plan_change_due_now: 'Due today',
    plan_change_prorated: 'Stripe bills the difference for the rest of this period, pro rata.',
    plan_change_from_next: 'From the next invoice you pay {price} per {interval}.',
    plan_change_drops_addon: '{addon} is not sold on this plan and will be switched off.',
    plan_change_confirm: 'Confirm switch',
    plan_change_done: 'You are now on {plan}',
    billing_cancels_on: 'Cancels on {date}',
    billing_what_you_pay: 'What you pay',

    // ===== inventory: navigation and shells =====
    inventory: 'Inventory',
    products: 'Products',
    products_sub: 'Everything you sell, goods and services.',
    stock_levels: 'Stock levels',
    stock_levels_sub: 'What you have, and where.',
    stock_movements: 'Stock movements',
    stock_movements_sub: 'Every in and out, in order. This log cannot be edited.',
    stock_adjustments: 'Stock adjustments',
    stock_adjustments_sub: 'How stock is corrected by hand. Every one leaves a record.',
    warehouses: 'Warehouses',
    warehouses_sub: 'Where the stock physically is.',
    warehouse: 'Warehouse',
    all_warehouses: 'All warehouses',
    new_warehouse: 'New warehouse',
    edit_warehouse: 'Edit warehouse',
    warehouse_code: 'Code',
    warehouse_address: 'Address',
    close_warehouse: 'Close warehouse',
    reopen_warehouse: 'Reopen',

    // ===== product =====
    product: 'Product',
    new_product: 'New product',
    edit_product: 'Edit product',
    product_details: 'Product details',
    basics: 'Basics',
    pricing: 'Pricing',
    kind_good: 'Good',
    kind_service: 'Service',
    kind_goods: 'Goods',
    kind_services: 'Services',
    kind_good_hint: 'Counted and valued in stock.',
    kind_service_hint: 'Sold by time or by job. Nothing to count.',
    sku: 'SKU',
    sku_hint: 'Leave blank to generate one.',
    unit: 'Unit',
    sale_price: 'Sale price',
    cost_hint: 'Used to value stock and post cost of sale.',
    cost_locked_hint: 'Set from receipts now. The average below is what stock is worth.',
    average_cost: 'Average cost',
    margin: 'Margin',
    base_currency_hint: 'Prices are in your base currency.',
    track_stock: 'Track stock for this product',
    track_stock_hint: 'Off for something you sell but never count, like a printed brochure.',
    track_stock_needs_module: 'Switch the Inventory product on to count this.',
    opening_stock: 'Opening stock',
    opening_stock_hint: 'Creates one stock adjustment dated today.',
    opening_unit_cost: 'Opening cost per unit',
    income_account: 'Income account',
    cogs_account: 'Cost of goods sold',
    inventory_account: 'Inventory account',
    sold_last_90: 'Sold in the last 90 days',
    not_sold_yet: 'Not sold yet',
    revenue_90: 'Revenue',
    cost_90: 'Cost',
    duplicate_product: 'Duplicate',
    archive: 'Archive',
    unarchive: 'Unarchive',
    archived: 'Archived',
    show_archived: 'Show archived',
    export_csv: 'Export CSV',
    change_category: 'Change category',
    manage_categories: 'Categories',
    new_category: 'New category',
    no_categories: 'No categories yet',
    n_selected: 'selected',
    select_row: 'Select row',
    select_all_page: 'Select all on this page',
    no_products: 'No products yet',
    no_products_sub:
      'Add the goods and services you sell. You can price them now and count the stock later.',
    no_products_match: 'No products match',
    no_products_match_sub: 'Try a different search, or clear the filters.',
    product_search_ph: 'Name or SKU',
    confirm_archive_products: 'Archive the selected products? They stay on every document they already appear on.',
    confirm_delete_product: 'Delete this product? Only possible while nothing references it.',

    // ===== product image =====
    image: 'Image',
    add_image: 'Add image',
    change_image: 'Change',
    remove_image: 'Remove',
    image_drop_hint: 'Drop, paste or click',
    image_removed: 'Image removed',
    image_pending: 'Uploaded when you save.',
    undo: 'Undo',
    image_too_big: 'Images only, up to 5 MB.',

    // ===== stock =====
    stock: 'Stock',
    on_hand: 'On hand',
    committed: 'Committed',
    available: 'Available',
    inventory_value: 'Inventory value',
    stock_value: 'Stock value',
    units_on_hand: 'Units on hand',
    products_tracked: 'Products tracked',
    across_all_units: 'across all units',
    moving_average: 'moving average',
    stock_by_warehouse: 'Stock by warehouse',
    negative_rows: 'Negative rows',
    negative_stock_hint: 'sold below what was received',
    no_stock_yet: 'No stock recorded yet',
    no_stock_counted: 'No stock counted yet',
    no_stock_counted_sub:
      'Count your stock once and every movement after it is tracked automatically.',
    no_warehouses: 'No warehouses yet',
    no_warehouses_sub: 'Add a warehouse before counting stock.',
    add_warehouse: 'Add warehouse',
    stock_in_stock: 'In stock',
    stock_low: 'Low',
    stock_out: 'Out of stock',
    stock_not_tracked: 'Not tracked',
    below_reorder_only: 'Below reorder only',

    // ===== movements =====
    movement_change: 'Change',
    movement_balance: 'Balance',
    movement_details: 'Movement',
    recent_movements: 'Recent movements',
    no_movements: 'No movements yet',
    no_movements_period: 'No movements in this period',
    no_movements_period_sub: 'Try a wider date range.',
    no_movements_ever: 'No stock movements yet',
    no_movements_ever_sub: 'Movements appear here as you sell, receive and count stock.',
    filtered_to_product: 'One product only',
    clear_filter: 'Clear',
    mv_purchase: 'Purchase',
    mv_sale: 'Sale',
    mv_adjustment: 'Count',
    mv_write_off: 'Write off',
    mv_opening: 'Opening',
    mv_transfer_in: 'Transfer in',
    mv_transfer_out: 'Transfer out',
    mv_reversal: 'Reversal',

    // ===== adjustments, the count flow =====
    adjustments: 'Adjustments',
    stock_adjustment: 'Stock adjustment',
    count_stock: 'Count stock',
    new_adjustment: 'New adjustment',
    edit_adjustment: 'Edit adjustment',
    adjustment_number: 'Adjustment #',
    adjustment_date: 'Adjustment date',
    counted_by: 'Recorded by',
    warehouse_locked_hint: 'Locked once you start counting.',
    system_qty: 'System',
    counted_qty: 'Counted',
    unit_cost: 'Unit cost',
    line_value: 'Value',
    not_counted: 'Not counted',
    n_counted: 'counted',
    filter_the_sheet: 'Filter the sheet…',
    review_counts: 'Review',
    post_count: 'Post',
    post_count_title: 'Post stock adjustment',
    post_count_warning: 'You are about to change stock. This cannot be undone.',
    products_change: 'products change',
    products_no_change: 'products matched and will not move.',
    net_value_change: 'Net',
    value_change: 'Value change',
    products_counted: 'Products counted',
    products_changed: 'Products changed',
    count_reason: 'Reason',
    reason_count: 'Counting correction',
    reason_write_off: 'Damaged, lost or expired',
    reason_opening: 'Opening balance',
    reason_count_hint: 'Type what you counted. We work out the difference.',
    reason_write_off_hint: 'Type the loss as a negative number. A write-off never adds stock.',
    reason_opening_hint: 'Stock that existed before Railit did. It posts as owner capital, not income.',
    count_attest: 'I counted this stock myself.',
    status_posted: 'Posted',
    adjust_stock: 'Adjust stock',
    nothing_to_count: 'Nothing to count in this warehouse',
    nothing_to_count_sub: 'Add products first, or pick another warehouse.',
    no_adjustments: 'No stock adjustments yet',
    no_adjustments_sub:
      'An adjustment is how stock is corrected by hand. Every one leaves a record.',
    no_adjustment_lines: 'Nothing counted yet',
    confirm_post_adjustment:
      'Post this adjustment? It writes the stock movements and the journal entry, and cannot be undone.',
    confirm_void_adjustment: 'Void this adjustment? Both the movements and the journal are reversed.',
    confirm_delete_adjustment: 'Delete this draft? Nothing has been posted yet.',
    pick_warehouse_first: 'Pick a warehouse to load the sheet.',

    // ===== low stock =====
    low_stock: 'Low stock',
    out_of_stock: 'Out of stock',
    below_reorder: 'Below reorder',
    reorder_point: 'Reorder point',
    reorder_point_hint: 'We flag it below this.',
    reorder_qty: 'Reorder quantity',
    suggested_order: 'Suggested order',
    low_stock_band: 'products are at or below their reorder point.',
    out_of_stock_band: 'products are out of stock.',
    show_them: 'Show them',
    dismiss: 'Dismiss',

    // ===== units, the closed list from lib/units.ts =====
    unit_unit: 'Unit',
    unit_piece: 'Piece',
    unit_kg: 'Kilogram',
    unit_g: 'Gram',
    unit_litre: 'Litre',
    unit_ml: 'Millilitre',
    unit_metre: 'Metre',
    unit_cm: 'Centimetre',
    unit_box: 'Box',
    unit_pack: 'Pack',
    unit_carton: 'Carton',
    unit_hour: 'Hour',
    unit_day: 'Day',

    // ===== server error codes =====
    item_sku_taken: 'That SKU is already used.',
    item_type_required: 'Say whether this is a good or a service.',
    item_in_use: 'This product appears on documents and cannot be deleted. Archive it instead.',
    item_has_stock: 'This product still has stock.',
    insufficient_stock: 'There is not enough stock for that.',
    module_off: 'The Inventory product is switched off for this company.',
    category_in_use: 'Products still use this category.',
  },
  ar: {
    dashboard: 'الرئيسية',
    accounting: 'المحاسبة',
    subscriptions: 'الاشتراكات',
    expenses: 'المصروفات',
    payroll: 'الرواتب',
    employees: 'الموظفون',
    contracts: 'العقود',
    invoices: 'الفواتير',
    quotations: 'عروض الأسعار',
    bills: 'الفواتير الواردة',
    payments: 'المدفوعات',
    bank: 'البنك',
    customers: 'العملاء',
    vendors: 'الموردون',
    chart_of_accounts: 'دليل الحسابات',
    journal: 'دفتر اليومية',
    reports: 'التقارير',
    users: 'المستخدمون والصلاحيات',
    settings: 'الإعدادات',
    finance: 'الماليّة',
    people: 'الموارد البشرية',
    sales: 'المبيعات',
    system: 'النظام',
    recurring: 'المتكررة',
    welcome: 'مرحبًا بعودتك',
    search: 'ابحث عن أي شيء…',
    new_: 'جديد',
    export_: 'تصدير',
    print: 'طباعة',
    revenue: 'الإيرادات',
    net_profit: 'صافي الربح',
    burn: 'الإنفاق الشهري',
    cash: 'الرصيد النقدي',
    name: 'الاسم',
    email: 'البريد',
    role: 'الدور',
    status: 'الحالة',
    amount: 'المبلغ',
    date: 'التاريخ',
    actions: 'إجراءات',
    add: 'إضافة',
    edit: 'تعديل',
    delete: 'حذف',
    save: 'حفظ',
    cancel: 'إلغاء',
    view: 'عرض',
    active: 'فعّال',
    paused: 'متوقف',
    overdue: 'متأخر',
    draft: 'مسودة',
    sent: 'مُرسل',
    signed: 'موقّع',
    paid: 'مدفوع',
    pending: 'قيد الانتظار',
    this_week: 'أسبوع',
    this_month: 'شهر',
    this_year: 'سنة',
    revenue_vs_expenses: 'الإيرادات مقابل المصروفات',
    vs_last: 'مقارنة بالشهر الماضي',
    quick_actions: 'إجراءات سريعة',
    recent: 'النشاط الأخير',
    bhd: 'BHD',
    usd: 'USD',
    collapse_sidebar: 'تصغير',
    expand_sidebar: 'توسيع',
    sign_in: 'تسجيل الدخول',
    sign_in_lede: 'استخدم بيانات الشركة. لم يتم دعوتك بعد؟ تواصل مع المدير.',
    password: 'كلمة المرور',
    keep_signed_in: 'ابقني مسجلاً',
    forgot: 'نسيت؟',
    continue_: 'متابعة',
    invalid_credentials: 'البريد أو كلمة المرور غير صحيحة.',
    internal_system: 'نظام داخلي — الوصول مقتصر على موظفي جوين فيوجر.',
    coming_soon: 'قريبًا',
    coming_soon_sub: 'هذه الوحدة ضمن مرحلة بناء لاحقة وليست متاحة بعد.',
    back_to_dashboard: 'العودة إلى الرئيسية',
    not_found: 'الصفحة غير موجودة',
    not_found_sub: 'المسار الذي حاولت فتحه غير موجود.',
    forbidden: 'غير مصرّح',
    forbidden_sub: 'دورك الحالي لا يملك صلاحية الوصول لهذه الصفحة.',
    logout: 'تسجيل الخروج',
    notifications: 'الإشعارات',
    loading: 'جاري التحميل',
    no_data: 'لا توجد بيانات بعد',
    dashboard_unavailable: 'بيانات لوحة التحكم غير متاحة حاليًا.',

    // shared / table
    search_placeholder: 'بحث…',
    show_inactive: 'عرض غير النشطين',
    only_active: 'النشطون فقط',
    all: 'الكل',
    yes: 'نعم',
    no: 'لا',
    optional: 'اختياري',
    confirm: 'تأكيد',
    saving: 'جارٍ الحفظ…',
    creating: 'جارٍ الإنشاء…',
    deleting: 'جارٍ الحذف…',
    inactive: 'غير نشط',
    restore: 'استعادة',
    set_default: 'تعيين كافتراضي',
    default_: 'افتراضي',
    none: 'لا يوجد',
    error_generic: 'حدث خطأ ما.',
    saved: 'تم الحفظ.',
    created: 'تم الإنشاء.',
    deleted: 'تم الحذف.',
    restored: 'تمت الاستعادة.',
    posted: 'تم الترحيل.',
    uploaded: 'تم الرفع.',
    uploading: 'جارٍ الرفع…',
    preview_pdf: 'معاينة PDF',
    attachments: 'المرفقات',
    no_attachments: 'لا توجد مرفقات بعد',
    confirm_delete_attachment: 'حذف هذا الملف؟',

    // customers / vendors
    new_customer: 'عميل جديد',
    edit_customer: 'تعديل العميل',
    new_vendor: 'مورّد جديد',
    edit_vendor: 'تعديل المورّد',
    customer_details: 'تفاصيل العميل',
    vendor_details: 'تفاصيل المورّد',
    name_ar: 'الاسم بالعربية',
    phone: 'الهاتف',
    vat_number: 'الرقم الضريبي',
    cr_number: 'السجل التجاري',
    address: 'العنوان',
    notes: 'ملاحظات',
    recent_invoices: 'أحدث الفواتير',
    recent_quotations: 'أحدث عروض الأسعار',
    recent_bills: 'أحدث فواتير المورد',
    recent_expenses: 'أحدث المصاريف',
    no_invoices: 'لا توجد فواتير بعد',
    no_bills: 'لا توجد فواتير مورد بعد',
    no_expenses: 'لا توجد مصاريف بعد',
    no_quotations: 'لا توجد عروض أسعار بعد',
    confirm_delete_customer: 'تعطيل هذا العميل؟',
    confirm_delete_vendor: 'تعطيل هذا المورّد؟',
    confirm_delete_account: 'حذف هذا الحساب؟ لا يمكن التراجع.',

    // accounts
    chart_of_accounts_sub: 'كل حسابات الأستاذ مجمّعة حسب النوع.',
    new_account: 'حساب جديد',
    edit_account: 'تعديل الحساب',
    code: 'الرمز',
    type: 'النوع',
    normal_balance: 'الرصيد الطبيعي',
    parent: 'الحساب الأب',
    debit: 'مدين',
    credit: 'دائن',
    asset: 'الأصول',
    liability: 'الخصوم',
    equity: 'حقوق الملكية',
    expense: 'المصروفات',
    balance: 'الرصيد',
    system_account: 'نظامي',

    // bank accounts
    bank_accounts: 'الحسابات البنكية',
    new_bank_account: 'حساب بنكي جديد',
    edit_bank_account: 'تعديل الحساب البنكي',
    opening_balance: 'الرصيد الافتتاحي',
    opening_balance_hint: 'اختياري. يسجّل رأس مال المالك في الدفاتر مقابل حقوق المالك.',
    bank_name: 'اسم البنك',
    iban: 'الآيبان',
    currency: 'العملة',

    // journal
    journal_viewer: 'سجل اليومية',
    post_manual_entry: 'ترحيل قيد يدوي',
    journal_entry: 'قيد محاسبي',
    entry_number: 'رقم القيد',
    entry_date: 'تاريخ القيد',
    memo: 'بيان',
    source: 'المصدر',
    total: 'الإجمالي',
    from_date: 'من',
    to_date: 'إلى',
    account: 'الحساب',
    add_line: 'إضافة سطر',
    remove: 'حذف',
    debits_total: 'المدين',
    credits_total: 'الدائن',
    balanced: 'متوازن',
    out_of_balance: 'غير متوازن',
    difference: 'الفرق',
    reversed: 'معكوس',
    manual: 'يدوي',
    invoice: 'فاتورة',
    bill: 'فاتورة موردين',
    payment: 'سداد',
    payroll_src: 'رواتب',
    subscription: 'اشتراك',

    // === Wave 3 Group A ===
    new_invoice: 'فاتورة جديدة',
    edit_invoice: 'تعديل الفاتورة',
    invoice_details: 'تفاصيل الفاتورة',
    customer: 'العميل',
    issue_date: 'تاريخ الإصدار',
    due_date: 'تاريخ الاستحقاق',
    bill_date: 'تاريخ الفاتورة',
    expires_at: 'تاريخ الانتهاء',
    expense_date: 'تاريخ المصروف',
    vendor: 'المورّد',
    category: 'الفئة',
    method: 'طريقة الدفع',
    bank_account: 'الحساب البنكي',
    bank_account_in: 'الحساب البنكي الذي سيستلم الدفعة',
    bank_account_out: 'الحساب البنكي الذي ستُصرف منه الدفعة',
    expense_account: 'حساب المصروف',
    description: 'الوصف',
    qty: 'الكمية',
    rate: 'السعر',
    line_amount: 'القيمة',
    subtotal: 'الإجمالي قبل الضريبة',
    vat: 'الضريبة',
    vat_rate: 'نسبة الضريبة',
    vat_amount: 'الضريبة',
    discount: 'الخصم',
    grand_total: 'الإجمالي',
    amount_paid: 'المدفوع',
    amount_due: 'المتبقي',
    total_due: 'المستحق',
    add_line_item: 'إضافة سطر',
    no_lines: 'لا توجد بنود بعد',
    select_customer: 'اختر العميل',
    select_vendor: 'اختر المورّد',
    select_account: 'اختر الحساب',
    select_bank_account: 'اختر الحساب البنكي',

    advance_section: 'الدفعة المقدمة',
    advance_type: 'فوترة دفعة مقدمة',
    advance_none: 'بدون — فوترة المبلغ كاملًا',
    advance_type_percent: 'نسبة من الصافي',
    advance_type_amount: 'مبلغ ثابت',
    advance_percent_value: 'نسبة الدفعة المقدمة %',
    advance_amount_value: 'قيمة الدفعة المقدمة',
    advance_percent_hint: '0–100 (مثال: 50 تعني 50%)',
    advance_amount_hint: 'بحد أقصى المجموع الفرعي ناقص الخصم',
    advance_custom_label_en: 'تسمية السطر (إنجليزي)',
    advance_custom_label_ar: 'تسمية السطر (عربي)',
    advance_default_label: 'دفعة مقدمة',
    advance_balance_later: 'الرصيد المتبقي للفوترة لاحقًا',
    advance_badge: 'دفعة مقدمة',
    advance_value_required: 'أدخل قيمة الدفعة المقدمة',
    advance_invalid: 'قيمة الدفعة المقدمة تقارب إلى صفر — ارفعها أو افوتر المبلغ كاملًا',
    advance_percent_invalid: 'لا يمكن أن تتجاوز الدفعة المقدمة 100%',
    advance_exceeds_net: 'لا يمكن أن تتجاوز الدفعة المقدمة المجموع الفرعي ناقص الخصم',
    advance_type_required: 'اختر نوع الدفعة المقدمة',

    new_quotation: 'عرض سعر جديد',
    edit_quotation: 'تعديل عرض السعر',
    quotation_details: 'تفاصيل عرض السعر',

    new_bill: 'فاتورة موردين جديدة',
    edit_bill: 'تعديل الفاتورة',
    bill_details: 'تفاصيل فاتورة المورد',

    new_expense: 'مصروف جديد',
    edit_expense: 'تعديل المصروف',
    expense_details: 'تفاصيل المصروف',

    send_: 'إرسال',
    sign_: 'توقيع',
    convert_to_invoice: 'تحويل إلى فاتورة',
    record_payment: 'تسجيل دفعة',
    void_: 'إلغاء',
    download_pdf: 'تحميل PDF',
    submit_: 'تقديم',
    approve_: 'اعتماد',
    reject_: 'رفض',
    post_: 'ترحيل',
    sent_ok: 'تم الإرسال.',
    signed_ok: 'تم التوقيع.',
    rejected_ok: 'تم الرفض.',
    voided_ok: 'تم الإلغاء.',
    payment_recorded: 'تم تسجيل الدفعة.',
    submitted_ok: 'تم التقديم.',
    approved_ok: 'تم الاعتماد.',
    converted_ok: 'تم التحويل إلى فاتورة.',

    status_draft: 'مسودة',
    status_sent: 'مُرسل',
    status_signed: 'موقّع',
    status_expired: 'منتهي',
    status_rejected: 'مرفوض',
    status_partial: 'مدفوع جزئيًا',
    status_paid: 'مدفوع',
    status_overdue: 'متأخر',
    status_void: 'ملغى',
    status_open: 'مفتوح',
    status_submitted: 'مُقدّم',
    status_approved: 'معتمد',

    filter_status: 'الحالة',
    all_statuses: 'كل الحالات',

    prev: 'السابق',
    next: 'التالي',
    page: 'صفحة',
    of: 'من',

    discount_exceeds_subtotal: 'لا يمكن أن يتجاوز الخصم المجموع الفرعي',
    invalid_url: 'أدخل رابطًا صحيحًا (https://…)',

    confirm_void_invoice: 'إلغاء هذه الفاتورة؟ سيتم عكس قيدها المحاسبي.',
    confirm_void_bill: 'إلغاء فاتورة المورد؟ سيتم عكس قيدها المحاسبي.',
    confirm_delete_invoice: 'حذف هذه المسودة؟',
    confirm_delete_quotation: 'حذف عرض السعر هذا؟',
    confirm_delete_bill: 'حذف مسودة الفاتورة؟',
    confirm_delete_expense: 'حذف هذا المصروف؟',
    confirm_reject_quotation: 'رفض عرض السعر؟',
    confirm_reject_expense: 'رفض هذا المصروف؟',

    payment_amount: 'المبلغ',
    paid_at: 'تاريخ السداد',
    payment_method: 'طريقة الدفع',
    payment_reference: 'المرجع',
    payment_memo: 'ملاحظات',
    method_bank: 'بنك',
    method_cash: 'نقدي',
    method_card: 'بطاقة',
    method_wire: 'حوالة',
    payments_history: 'الدفعات',
    no_payments: 'لا توجد دفعات بعد',
    journal_summary: 'القيد المحاسبي',
    no_journal_entry: 'لم يتم ترحيل قيد بعد',

    converted_invoice: 'الفاتورة المُولّدة',
    convert: 'تحويل',
    notes_optional: 'ملاحظات (اختياري)',
    line_subtotal: 'الإجمالي قبل الضريبة',
    line_total_due: 'المتبقي',
    invoice_summary: 'الملخص',
    quotation_summary: 'الملخص',
    bill_summary: 'الملخص',
    expense_summary: 'الملخص',
    submitted_by: 'قدّمها',
    approved_by: 'اعتمدها',
    receipt_url: 'رابط الإيصال',
    save_draft: 'حفظ كمسودة',

    // === Wave 3 Group B ===
    // employees
    new_employee: 'موظف جديد',
    edit_employee: 'تعديل الموظف',
    employee_details: 'تفاصيل الموظف',
    employee_number: 'رقم الموظف',
    department: 'القسم',
    contract_type: 'نوع العقد',
    base_salary: 'الراتب الأساسي',
    joined_at: 'تاريخ الانضمام',
    terminated_at: 'تاريخ إنهاء الخدمة',
    full_time: 'دوام كامل',
    part_time: 'دوام جزئي',
    contractor: 'متعاقد',
    terminated: 'منتهٍ',
    terminate: 'إنهاء الخدمة',
    confirm_terminate_employee:
      'إنهاء خدمة هذا الموظف؟ سيتم تعطيل أي حساب مستخدم مرتبط وإنهاء الجلسات. لا يمكن التراجع.',
    recent_payslips: 'أحدث كشوف الرواتب',
    no_payslips: 'لا توجد كشوف رواتب بعد',
    period: 'الفترة',
    gross: 'الإجمالي',
    deductions: 'الاستقطاعات',
    net_amount: 'الصافي',
    linked_user: 'المستخدم المرتبط',
    no_linked_user: 'لا يوجد مستخدم مرتبط',
    // payroll
    payroll_runs: 'دورات الرواتب',
    new_payroll_run: 'دورة رواتب جديدة',
    payroll_run: 'دورة رواتب',
    payroll_run_details: 'تفاصيل دورة الرواتب',
    period_start: 'بداية الفترة',
    period_end: 'نهاية الفترة',
    pay_date: 'تاريخ الصرف',
    total_gross: 'إجمالي الراتب',
    total_deductions: 'إجمالي الاستقطاعات',
    total_net: 'إجمالي الصافي',
    payslips: 'كشوف الرواتب',
    employee: 'الموظف',
    payment_status: 'حالة الدفع',
    post_run: 'ترحيل الدورة',
    pay_run: 'صرف الدورة',
    delete_run: 'حذف الدورة',
    confirm_delete_payroll_run: 'حذف هذه المسودة لدورة الرواتب؟',
    confirm_post_payroll_run:
      'ترحيل هذه الدورة؟ سيتم ترحيل كل كشف راتب للأستاذ ولن يمكن حذفه بعد ذلك.',
    pick_bank_account: 'اختر حسابًا بنكيًا',
    pay_payroll_title: 'صرف دورة الرواتب',
    optional_employees: 'موظفون محددون (اختياري)',
    optional_employees_hint: 'اتركه فارغًا لتضمين كل الموظفين النشطين.',
    no_payslips_in_run: 'لا توجد كشوف رواتب في هذه الدورة',
    // subscriptions
    new_subscription: 'اشتراك جديد',
    edit_subscription: 'تعديل الاشتراك',
    subscription_details: 'تفاصيل الاشتراك',
    service: 'الخدمة',
    cost: 'التكلفة',
    cycle: 'الدورة',
    monthly: 'شهري',
    quarterly: 'ربع سنوي',
    yearly: 'سنوي',
    auto_charge: 'فوترة تلقائية',
    auto: 'تلقائي',
    manual_charge: 'يدوي',
    pick_vendor: 'اختر موردًا',
    pick_expense_account: 'اختر حساب مصروف',
    owner: 'المسؤول',
    pick_owner_employee: 'اختر موظفًا مسؤولًا',
    pause: 'إيقاف مؤقت',
    resume: 'استئناف',
    charge_now: 'فوترة الآن',
    recent_charges: 'أحدث الفواتير',
    no_charges: 'لا توجد فواتير بعد',
    charged_at: 'تاريخ الفوترة',
    cancelled: 'ملغى',
    confirm_cancel_subscription: 'إلغاء هذا الاشتراك؟ ستتوقف الفواتير الجديدة.',
    confirm_charge_now:
      'إنشاء فاتورة موردين لهذا الاشتراك الآن؟ سيتم تقديم تاريخ الفوترة التالية.',
    confirm_delete_subscription: 'حذف هذا الاشتراك؟',
    // contracts
    new_contract: 'عقد جديد',
    edit_contract: 'تعديل العقد',
    contract_details: 'تفاصيل العقد',
    party: 'الطرف',
    party_name: 'اسم الطرف',
    kind: 'النوع',
    contract_value: 'القيمة',
    ends_at: 'تاريخ الانتهاء',
    body: 'النص',
    body_md: 'النص (إنجليزي - Markdown)',
    body_md_ar: 'النص (عربي - Markdown)',
    service_agreement: 'اتفاقية خدمات',
    saas_subscription: 'اشتراك SaaS',
    employment: 'توظيف',
    nda: 'اتفاقية سرية',
    mou: 'مذكرة تفاهم',
    other: 'أخرى',
    expired: 'منتهي',
    sign: 'توقيع',
    signed_at: 'تاريخ التوقيع',
    confirm_sign_contract: 'وضع هذا العقد كموقّع؟',
    confirm_terminate_contract: 'إنهاء هذا العقد؟ لا يمكن التراجع.',
    confirm_delete_contract: 'حذف هذه المسودة من العقد؟',
    // contract e-signature & templates
    esign_employer: 'توقيع صاحب العمل',
    esign_employee: 'توقيع الموظف',
    esign_sign_here: 'وقّع هنا',
    esign_clear: 'مسح',
    esign_submit: 'إرسال التوقيع',
    esign_name_placeholder: 'الاسم الكامل',
    esign_signed_by: 'وقّع بواسطة',
    esign_signed_on: 'تاريخ التوقيع',
    esign_awaiting: 'بانتظار التوقيع',
    esign_saved: 'تم حفظ التوقيع.',
    contract_templates: 'قوالب العقود',
    new_template: 'قالب جديد',
    load_template: 'تحميل قالب',
    template_name: 'اسم القالب',
    template_name_ar: 'اسم القالب بالعربية',
    select_template: 'اختر قالبًا…',
    it_employment_template: 'توظيف تقني (جزئي)',
    linked_employee: 'الموظف المرتبط',
    select_employee: 'اختر موظفًا',
    cpr: 'الرقم الشخصي (CPR)',
    employee_contracts: 'العقود',
    no_contracts: 'لا توجد عقود بعد',
    pdf_preview: 'معاينة PDF',

    // === Wave 3 Group C ===
    // Settings
    settings_company: 'الشركة',
    settings_tax: 'الضرائب',
    settings_branding: 'الهوية',
    settings_subtitle: 'بيانات الشركة، إعدادات الضريبة الافتراضية، وهوية الفواتير.',
    company_name: 'اسم الشركة',
    company_name_ar: 'اسم الشركة بالعربية',
    default_currency: 'العملة الافتراضية',
    language: 'اللغة',
    default_vat_rate: 'نسبة الضريبة الافتراضية (%)',
    fy_start_month: 'بداية السنة المالية',
    invoice_terms: 'شروط وأحكام الفواتير',
    bank_iban: 'الآيبان البنكي',
    payment_details: 'بيانات الدفع',
    document_bank_account: 'الحساب البنكي في المستندات',
    document_bank_account_custom: 'يدوي',
    document_bank_account_hint: 'هذا هو الحساب الذي يُطلب من العملاء التحويل إليه في فواتيرهم.',
    document_bank_account_empty: 'لا توجد حسابات بنكية بعد — أضف حسابًا أولًا من',
    settings_saved: 'تم حفظ الإعدادات.',
    month_jan: 'يناير',
    month_feb: 'فبراير',
    month_mar: 'مارس',
    month_apr: 'أبريل',
    month_may: 'مايو',
    month_jun: 'يونيو',
    month_jul: 'يوليو',
    month_aug: 'أغسطس',
    month_sep: 'سبتمبر',
    month_oct: 'أكتوبر',
    month_nov: 'نوفمبر',
    month_dec: 'ديسمبر',

    // Users
    users_subtitle: 'إدارة من يستطيع الدخول وما يمكنه فعله.',
    invite_heading: 'انضم إلى {org} على Railit',
    invite_body: 'دُعيت بصلاحية {role}. هذا الرابط يخص {email}.',
    invite_existing_account: 'لديك حساب Railit بالفعل. القبول يضيف هذه الشركة إليه، وتنتقل بينها وبين شركاتك من أعلى الشاشة.',
    invite_accept: 'اقبل وافتح المساحة',
    invite_invalid_title: 'هذه الدعوة لم تعد صالحة',
    invite_invalid_body: 'ربما انتهت صلاحيتها أو استُخدمت أو سُحبت. اطلب ممن دعاك إرسال دعوة جديدة.',
    wa_country: 'الدولة',
    wa_country_code: 'مفتاح الدولة',
    notes_empty: 'لا توجد ملاحظات على هذا السجل.',
    notes_placeholder: 'شيء يحتاج معرفته من يفتح هذا السجل بعد ستة أشهر.',
    note_add: 'أضف ملاحظة',
    note_by_ai: 'الذكاء الاصطناعي',
    note_asked_for_by: 'بطلب من',
    note_written_by: 'بقلم',
    wa_other_country: 'دولة أخرى',
    wa_whose_phone: 'هاتف من',
    wa_my_own_phone: 'هاتفي أنا',
    wa_number_local: 'رقم الهاتف',
    wa_will_link: 'سيُربط الرقم',
    wa_my_numbers: 'أرقامي',
    wa_my_numbers_lede: 'أضف رقمك وتحدّث مع المساعد من واتساب. الأرقام الإضافية مجانية. يجيبك عن هذه الشركة، ويسألك أيّها تقصد إن كنت تدير أكثر من دفتر.',
    wa_assistant_number: 'راسل المساعد على',
    wa_add_number: 'أضف رقمي',
    wa_add_number_hint: 'أرسل له رسالة من ذلك الهاتف بعدها. أول رسالة هي ما يُثبت أن الرقم رقمك.',
    wa_number_added: 'أُضيف الرقم. أرسل رسالة من الهاتف لإكمال الربط.',
    wa_verified: 'موثّق',
    wa_awaiting_first_message: 'بانتظار أول رسالة منك',
    wa_no_numbers: 'لا توجد أرقام بعد.',
    wa_no_usage_yet: 'لم يُصرف من هذا الرقم شيء بعد.',
    wa_credits_30d: 'رصيد خلال ٣٠ يومًا',
    wa_turns_30d: 'محادثة',
    wa_last_used: 'آخر استخدام',
    add_user: 'أضف مستخدمًا',
    send_invitation: 'أرسل الدعوة',
    invite_link_hint: 'الدعوة في طريقها. إن لم يصل البريد، أرسل له هذا الرابط:',
    new_user: 'مستخدم جديد',
    edit_user: 'تعديل المستخدم',
    reset_password: 'إعادة تعيين كلمة المرور',
    disable_user: 'تعطيل المستخدم',
    confirm_disable_user: 'تعطيل هذا المستخدم؟ لن يتمكن من تسجيل الدخول.',
    user_disabled: 'تم تعطيل المستخدم.',
    password_reset_ok: 'تم تحديث كلمة المرور.',
    user_created: 'تم إنشاء المستخدم.',
    user_updated: 'تم تحديث المستخدم.',
    role_owner: 'مالك',
    role_admin: 'مدير',
    role_finance: 'مالية',
    role_engineer: 'مهندس',
    role_sales: 'مبيعات',
    role_designer: 'مصمم',
    role_viewer: 'مشاهد',
    mfa: 'التحقق الثنائي',
    mfa_enabled: 'مفعّل',
    mfa_disabled: 'غير مفعّل',
    last_login_at: 'آخر دخول',
    new_password: 'كلمة المرور الجديدة',
    password_min_8: '٨ أحرف على الأقل، وتشمل حرفًا ورقمًا.',
    never_signed_in: 'لم يدخل بعد',

    // Dashboard (extra)
    revenue_mtd: 'إيرادات الشهر',
    profit_mtd: 'ربح الشهر',
    monthly_burn: 'الإنفاق الشهري',
    cash_on_hand: 'الرصيد النقدي',
    recent_activity: 'النشاط الأخير',
    upcoming: 'القادم',
    overdue_invoices: 'فواتير متأخرة',
    subs_due_7d: 'اشتراكات تستحق خلال 7 أيام',
    no_recent_activity: 'لا يوجد نشاط حديث',
    no_overdue_invoices: 'لا توجد فواتير متأخرة',
    no_upcoming_subs: 'لا توجد اشتراكات هذا الأسبوع',
    next_charge: 'موعد التجديد',
    today: 'اليوم',
    custom: 'مخصص',
    income_and_expense: 'الإيرادات والمصروفات',
    top_expenses: 'أعلى المصروفات',
    all_expenses: 'إجمالي المصروفات',
    total_receivables: 'إجمالي الذمم المدينة',
    total_payables: 'إجمالي الذمم الدائنة',
    current: 'جارية',
    cash_flow: 'التدفق النقدي',
    incoming: 'وارد',
    outgoing: 'صادر',
    net_cash: 'صافي النقد',
    profit: 'الربح',

    // Reports
    reports_subtitle: 'القوائم المالية، أعمار الذمم، وتصدير سريع PDF.',
    report_pnl: 'الأرباح والخسائر',
    report_balance_sheet: 'الميزانية العمومية',
    report_cashflow: 'التدفقات النقدية',
    report_ar_aging: 'أعمار الذمم المدينة',
    report_pnl_sub: 'الإيرادات والمصروفات وصافي الربح للفترة المختارة.',
    report_balance_sheet_sub: 'الأصول والخصوم وحقوق الملكية في تاريخ محدد.',
    report_cashflow_sub: 'تدفقات النقد الداخلة والخارجة خلال الفترة.',
    report_ar_aging_sub: 'الذمم المدينة المتبقية مجمّعة حسب العمر.',
    quick_pdfs: 'تقارير PDF سريعة',
    weekly_pdf: 'التقرير الأسبوعي (PDF)',
    daily_pdf: 'التقرير اليومي (PDF)',
    weekly_pdf_sub: 'إنشاء ملخص الإدارة لهذا الأسبوع.',
    daily_pdf_sub: 'إنشاء لقطة اليوم.',
    download: 'تحميل',
    pdf_downloaded: 'تم تحميل الملف.',
    week_number: 'رقم الأسبوع',
    year: 'السنة',
    prepared_by: 'إعداد',
    pnl_revenue: 'الإيرادات',
    pnl_expenses: 'المصروفات',
    pnl_net: 'صافي الربح',
    bs_assets: 'الأصول',
    bs_liabilities: 'الخصوم',
    bs_equity: 'حقوق الملكية',
    cf_opening: 'الرصيد الافتتاحي',
    cf_inflows: 'التدفقات الداخلة',
    cf_outflows: 'التدفقات الخارجة',
    cf_closing: 'الرصيد الختامي',
    cf_total_in: 'إجمالي الداخل',
    cf_total_out: 'إجمالي الخارج',
    aging_as_of: 'بتاريخ',
    no_invoices_in_bucket: 'لا توجد فواتير في هذه الفئة',
    as_of: 'بتاريخ',

    // الإعدادات: كلود / MCP
    settings_mcp: 'كلود / MCP',
    mcp_title: 'تكامل كلود / MCP',
    mcp_status_connected: 'متصل',
    mcp_status_not_installed: 'غير مثبّت',
    mcp_tools: 'أداة',
    mcp_last_sync: 'آخر مزامنة',
    mcp_api_base: 'عنوان الـ API',
    mcp_install_path: 'مسار الخادم',
    mcp_capabilities_title: 'ماذا يستطيع كلود أن يفعل',
    mcp_cap_upload_docs: 'رفع المستندات',
    mcp_cap_send_pdf: 'إنشاء وإرسال ملفات PDF',
    mcp_cap_full_crud: 'تحكّم كامل بكل البيانات',
    mcp_cap_audit: 'كل إجراء مُسجَّل في سجل التدقيق',
    mcp_tool_groups: 'مجموعات الأدوات',
    mcp_setup_title: 'كيفية ربط Claude Desktop',
    mcp_config_path: 'ملف إعدادات Claude Desktop',
    mcp_config_snippet: 'مقتطف الإعدادات (الصقه في الملف أعلاه)',
    mcp_copy: 'نسخ',
    mcp_copied: 'تم النسخ',
    mcp_os_mac: 'ماك',
    mcp_os_win: 'ويندوز',
    mcp_os_linux: 'لينكس',
    mcp_step_1: 'انسخ أو استنسخ المستودع إلى جهازك.',
    mcp_step_3: 'افتح ملف إعدادات Claude Desktop من المسار أعلاه (أنشئه إذا لم يكن موجوداً).',
    mcp_step_4: 'أنشئ رمز API أعلاه ثم الصقه في المقتطف بقيمة JOINFUTURE_API_TOKEN (بدلاً من jf_PASTE_TOKEN_HERE).',
    mcp_step_5: 'أعد تشغيل Claude Desktop وستظهر أدوات joinfuture في لوحة الأدوات.',
    mcp_examples_title: 'أمثلة على الأوامر التي يمكنك إعطاؤها لكلود',
    mcp_security_note: 'يحفظ ملف الإعدادات رمز الـ API بصيغة نصية على هذا الجهاز. أي شخص يصل إليه يمكنه التصرّف باسمك في الـ ERP. أبقِ جهازك مُقفلاً، ولا ترفع الملف إلى Git، وألغِ الرمز من هذه الصفحة لقطع الاتصال.',
    mcp_api_tokens: 'رموز الـ API',
    mcp_token_name: 'اسم الرمز',
    mcp_generate: 'إنشاء',
    mcp_copy_once: 'انسخه الآن — لن يظهر مرة أخرى.',
    mcp_token: 'الرمز',
    mcp_no_tokens: 'لا توجد رموز بعد. أنشئ واحداً لربط كلود.',
    mcp_revoke: 'إلغاء',
    mcp_token_never_used: 'لم يُستخدم',
    mcp_token_last_used: 'آخر استخدام',
    mcp_token_revoked: 'أُلغي',
    mcp_connect_any_ai: 'اربط أي مساعد ذكاء اصطناعي',
    mcp_connect_any_ai_sub: 'نظام واحد، كل المساعدين. اختر منصّة — Claude.ai أو ChatGPT أو Gemini أو Cursor — وانسخ المقتطف.',
    mcp_remote_endpoint: 'النقطة البعيدة',
    mcp_auth: 'مصادقة',
    mcp_setup_badge: '(إعداد)',
    mcp_json_config: 'إعداد JSON',
    net_income_trend: 'اتجاه صافي الدخل',
    access_denied: 'الوصول مرفوض',
    recent_subscriptions: 'الاشتراكات الأخيرة',
    no_subscriptions: 'لا توجد اشتراكات',
    enable_user: 'تفعيل المستخدم',
    user_enabled: 'تم تفعيل المستخدم.',
    subs_mtd: 'منذ بداية الشهر',
    live_cost_from: 'مباشر من',
    live_cost_updated: 'مُحدّث',
    live_cost_integration: 'التكامل',

    // ===== Railit SaaS =====
    crm: 'العملاء والمبيعات',
    org_settings: 'إعدادات المنشأة',
    settings_organization: 'المنشأة',
    settings_billing: 'الاشتراك',
    settings_whatsapp: 'واتساب',

    // auth
    create_account: 'أنشئ حسابك',
    signup_lede: 'مساحة عملك جاهزة خلال أقل من دقيقة. 14 يومًا مجانًا وبدون بطاقة.',
    signup_failed: 'تعذّر إنشاء الحساب. حاول مرة أخرى.',
    company_name_required: 'أدخل اسم الشركة',
    your_name: 'اسمك',
    name_required: 'أدخل اسمك',
    work_email: 'البريد الإلكتروني للعمل',
    email_invalid: 'أدخل بريدًا صحيحًا',
    password_min: '8 أحرف على الأقل',
    start_free_trial: 'ابدأ التجربة المجانية',
    have_account: 'لديك حساب؟',
    no_account: 'ليس لديك حساب؟',
    trial: 'التجربة',
    trial_days_n: '{n} أيام مجانًا',
    no_card_needed: 'بدون بطاقة',
    from_price: 'ابتداءً من',
    per_month: 'شهريًا',
    price_extra_user: '+ 4 دولار لكل مستخدم إضافي',

    // modules
    mod_finance: 'المالية',
    mod_finance_sub: 'فواتير ومطالبات وضريبة وقيود',
    mod_crm: 'إدارة العملاء',
    mod_crm_sub: 'العملاء وعروض الأسعار والعقود',
    mod_hr: 'الموارد البشرية',
    mod_hr_sub: 'الموظفون والرواتب وقسائم الراتب',
    mod_inventory: 'المخزون',
    mod_inventory_sub: 'الأصناف والكميات والحركات',
    mod_pos: 'نقاط البيع',
    mod_pos_sub: 'مبيعات الكاشير تُرحَّل للقيود',
    mod_ai: 'وكلاء الذكاء الاصطناعي',
    mod_ai_sub: 'محادثة وMCP وواتساب',

    // billing
    billing_base: 'الاشتراك',
    billing_extra_users: 'مستخدمون إضافيون ({n})',
    billing_monthly_total: 'الإجمالي الشهري',
    billing_renews_on: 'يتجدد في {date}',
    billing_manage: 'إدارة الاشتراك',
    billing_subscribe: 'اشترك الآن',
    billing_addons: 'الإضافات',
    billing_seats: 'المستخدمون',
    billing_seats_detail: '{used} مستخدم مدفوع. أول {included} مشمول في الاشتراك، وكل إضافي يُحتسب شهريًا.',
    billing_seats_short: '{used} مستخدم مدفوع · {price} شهريًا',
    agent_session_cost: 'الكريدت المستهلك في هذه المحادثة',
    agent_turn_cost: '{n} كريدت',
    agent_attach: 'إرفاق',
    agent_voice: 'صوت',
    agent_stop: 'إيقاف',
    agent: 'الوكيل',
    agent_new: 'محادثة جديدة',
    agent_history: 'السجل',
    agent_wide: 'توسيع',
    agent_narrow: 'تضييق',
    agent_send: 'إرسال',
    agent_greeting: 'كيف أساعدك؟',
    agent_scope: 'يعمل داخل {org}',
    agent_placeholder: 'اسأل عن أي شيء…',
    agent_not_wired: 'لم يُوصَل الوكيل بعد.',
    agent_failed: 'حدث خطأ. حاول مرة أخرى.',
    agent_no_credits: 'نفد كريدت الذكاء الاصطناعي في مساحة العمل. أضف المزيد من صفحة الفوترة.',
    agent_stopped_budget: 'توقّف — المهمة أطول مما يسمح به طلب واحد.',
    agent_stopped_credits: 'توقّف مبكرًا ليبقى لديك رصيد كريدت.',
    agent_files: 'الملفات',
    agent_resize: 'تغيير عرض اللوحة',
    agent_expand_full: 'افتح في وضع الوكيل',
    agent_snap_release: 'أفلت للانتقال إلى وضع الوكيل',
    agent_snap_confirm: 'اضغط Enter للانتقال إلى وضع الوكيل',
    agent_doc_loading: 'يُجهَّز…',
    agent_doc_download: 'تنزيل',
    agent_doc_failed: 'تعذّر فتح هذا المستند',
    agent_file_unread: 'لم يُقرأ',
    agent_file_too_big: 'حجم {name} أكبر من 10 ميجابايت.',
    agent_remove_file: 'إزالة',
    agent_reading: 'يقرأ ملفاتك…',
    agent_thinking: 'يعمل…',
    agent_proposed: 'تغيير مقترح',
    agent_apply: 'تنفيذ',
    agent_discard: 'تجاهل',
    agent_applied: 'تم التنفيذ',
    agent_discarded: 'تم التجاهل',
    agent_apply_failed: 'تعذّر التنفيذ. ربما انتهت صلاحية الاقتراح.',
    agent_not_applied: 'لم يُحفظ أي شيء بعد.',
    agent_steps: '{n} خطوات',
    agent_steps_one: 'خطوة واحدة',
    agent_steps_failed: 'فشلت واحدة',
    agent_no_history: 'لا توجد محادثات بعد.',
    agent_untitled: 'بدون عنوان',
    agent_when_today: 'اليوم',
    agent_when_yesterday: 'أمس',
    agent_when_week: 'آخر 7 أيام',
    agent_when_older: 'أقدم',
    vs_last_month: 'مقارنة بنفس الفترة الشهر الماضي',
    recurring_commitments: 'التزامات متكررة',
    across_accounts: 'في جميع الحسابات',
    pct_overdue: '{n}% من هذا المبلغ متأخر بالفعل',
    view_all: 'عرض الكل',
    pdf_failed: 'تعذّر إنشاء ملف PDF. حاول مرة أخرى.',
    agent_threads: 'المحادثات',
    agent_greeting_hint: 'اسأل عن دفاترك، أو أرفق ملفًا لنعمل عليه.',
    agent_mode: 'وضع الوكيل',
    agent_mode_on: 'وكيل',
    agent_mode_off: 'يدوي',
    agent_mode_hint: 'حوّل مساحة العمل كلها إلى محادثة.',
    model_auto: 'تلقائي',
    model_auto_hint: 'يختار المستوى المناسب لكل سؤال — الأرخص للبحث، والأدق للتسويات.',
    model_pick_hint: 'بأي دقة تريد أن يعمل الوكيل؟',
    model_per_month: '~{n} سؤال شهريًا',
    model_credits_each: '{n} كريدت للسؤال',
    model_thinking: 'تفكير',
    model_thinking_hint: 'يفكّر خطوة بخطوة قبل الإجابة. أبطأ، وبضعف الكريدت تقريبًا.',
    model_thinking_cost: 'نحو {n} كريدت للسؤال مع التفكير.',
    thinking_live: 'يفكّر…',
    thinking_done: 'فكّر في هذا',
    memory: 'الذاكرة',
    memory_title: 'ما يتذكّره الوكيل',
    memory_subtitle: 'حقائق يحتفظ بها بين المحادثات حتى لا تكررها. احذف أي شيء خاطئ أو قديم.',
    memory_company: 'عن شركتك',
    memory_company_hint: 'يراها كل من في مساحة العمل',
    memory_personal: 'عنك أنت',
    memory_personal_hint: 'تراها أنت فقط',
    memory_by_you: 'أنت كتبتها',
    memory_by_agent: 'الوكيل دوّنها',
    memory_used: 'استُخدمت في {n} محادثة',
    memory_unused: 'لم تُستخدم بعد',
    memory_active: 'مفعّلة',
    memory_export: 'تصدير كـ Markdown',
    memory_empty: 'لا شيء محفوظ بعد',
    memory_empty_hint: 'أخبر الوكيل بشيء مثل «نحن دائمًا نفوتر بمهلة 45 يومًا» وسيحتفظ به.',
    memory_deleted: 'تم النسيان.',
    memory_failed: 'تعذّر تحديث هذه الذاكرة.',
    memory_upload: 'رفع ملف .md',
    memory_uploaded: 'تمت إضافة {n} ملفات.',
    memory_kind_fact: 'حقيقة',
    memory_kind_note: 'ملاحظة',
    memory_kind_skill: 'طريقة عمل',
    memory_kind_fact_hint: 'قصيرة بما يكفي ليحملها الوكيل في كل محادثة.',
    memory_kind_note_hint: 'مستند. الوكيل يرى الوصف فقط، ويفتحه عندما يبدو مناسبًا.',
    memory_kind_skill_hint: 'إجراء. يقرأه الوكيل عندما يوشك على تنفيذ هذا الشيء.',
    memory_total_cost: 'كل هذا يضيف {tok} توكن لكل سؤال — نحو {cr} كريدت للدور.',
    memory_carried: 'محمولة في كل محادثة · {tok} توكن',
    memory_opened: 'فُتحت {n} مرة',
    memory_never_opened: 'لم تُفتح قط — كلّفت {cr} كريدت',
    memory_cost_hint: 'تضيف {tok} توكن لكل سؤال. {cr} كريدت حتى الآن.',
    memory_when_used: 'يفتحه الوكيل عندما',
    memory_file: 'الملف',
    memory_words: '{n} كلمة — تُفتح فقط عندما يطابق الوصف أعلاه ما يُسأل عنه',
    billing_credits_left: 'كريدت متبقٍ',
    billing_credits_top_up: 'أوشك على النفاد — أضِف رصيدًا',
    billing_credits_trial: 'متبقٍ من تجربتك',
    billing_credits_from_topup: 'متبقٍ من رصيدك المشترى',
    account_avatar_hint: 'اضغط أو أفلِت صورة PNG أو JPEG أو WebP. حتى 2 ميجابايت.',
    billing_team: 'حجم الفريق',
    billing_team_lede: 'اشترِ مقاعد قبل شغلها — المقعد المدفوع لا يكلّفك شيئًا عند شغله.',
    billing_seats_using: '{n} في مساحة العمل حاليًا',
    billing_seats_included_then: '{n} مشمول، ثم {price} لكل مستخدم',
    billing_seats_ahead: 'شراء {n} مقعد إضافي مسبقًا.',
    billing_seats_fewer: 'مقاعد أقل',
    billing_seats_more: 'مقاعد أكثر',
    billing_then_from_second: 'خصم {pct}%. ومن الشهر الثاني يصبح {price}.',
    purchases: 'المشتريات',
    money: 'الأموال',
    workspace: 'مساحة العمل',
    doc_accent_from_logo: 'اختر من الشعار',
    doc_accent_pick_failed: 'تعذّر استخراج لون من هذا الشعار.',
    per_month_short: ' /شهر',
    billing_billed_yearly: '{total} تُدفع سنويًا',
    billing_billed_yearly_total: '{total} تُخصم مرة واحدة في السنة',
    billing_delta_month: 'شهريًا',
    billing_switch_to_yearly: 'التحويل إلى الدفع السنوي',
    billing_switch_to_monthly: 'التحويل إلى الدفع الشهري',
    done: 'تم',
    error: 'حدث خطأ ما.',
    billing: 'الاشتراك',
    billing_configure_lede: 'اضبط اشتراكك على اليسار. والفاتورة على اليمين هي المبلغ الذي ستُحاسب عليه بالضبط.',
    billing_plan: 'الباقة',
    billing_plan_lede: 'كل ما بعدها يُسعَّر بناءً عليها.',
    billing_popular: 'الأكثر اختيارًا',
    billing_months_free: '{n} شهران مجانًا',
    billing_credits_monthly: '{n} كريدت ذكاء اصطناعي شهريًا',
    billing_first_month_is: 'الشهر الأول {price}',
    billing_ai: 'رصيد الذكاء الاصطناعي',
    billing_ai_monthly_lede: 'مخصّصك الشهري. يتجدّد كل دورة ولا يُرحَّل.',
    billing_included: 'مشمول',
    billing_included_in_plan: 'يأتي مع الباقة',
    billing_this_month: 'المستهلك هذه الدورة',
    billing_ai_model_note: 'يعمل على {model}',
    billing_purchased_left: 'بقي {n} كريدت مشترى',
    billing_addons_lede: 'منتجات يمكنك تشغيلها وإيقافها.',
    billing_addon_brings: '{messages} رسالة شهريًا. والذكاء الاصطناعي يُخصم من رصيد باقتك.',
    billing_addon_needs_plan: 'متاح في باقة {plan} فما فوق',
    billing_topup: 'باقات كريدت',
    billing_topup_lede: 'لحين نفاد المخصّص قبل انتهاء الشهر.',
    billing_topup_separate: 'تُشترى مرة واحدة ولا تنتهي صلاحيتها، وتُستهلك بعد نفاد المخصّص الشهري فقط. وهي ليست جزءًا من الاشتراك.',
    billing_approx_exchanges: 'نحو {n} محادثة',
    billing_percent_off: 'خصم {n}%',
    billing_your_bill: 'فاتورتك',
    billing_bill_lede_month: 'تُحاسب شهريًا. غيّر أو ألغِ في أي وقت.',
    billing_bill_lede_year: 'تُحاسب مرة كل سنة. عشرة أشهر مدفوعة وشهران مجانًا.',
    billing_seats_note: '{used} مستخدمين، {included} مشمول في الباقة',
    billing_total_month: 'شهريًا',
    billing_total_year: 'سنويًا',
    billing_first_month: 'شهرك الأول',
    billing_then_renews: 'خصم {pct}%. ثم يتجدّد بـ {price}.',
    billing_checkout: 'المتابعة إلى الدفع',
    billing_update: 'تحديث الاشتراك',
    billing_no_changes: 'لا تغييرات لتطبيقها',
    billing_updated: 'تم تحديث اشتراكك.',
    billing_owner_only: 'المالك وحده يمكنه تغيير الاشتراك.',
    billing_trial_free: 'مجانًا لمدة {days} أيام. بدون بطاقة حتى الآن.',
    billing_trial_running: 'تجربتك مجانية حتى {date}. ولن تحتاج بطاقة إلا للاستمرار بعدها.',
    billing_renews_plain: 'يتجدّد في {date}',
    billing_confirm_change: 'تأكيد التغيير',
    billing_confirm_change_lede: 'يحتسب سترايب الفرق بالتناسب مع ما دفعته لهذه الدورة.',
    billing_due_now: 'يُخصم اليوم',
    billing_due_now_unknown: 'يُحتسب عند الدفع',
    billing_next_invoice: 'الفاتورة القادمة',
    billing_confirm: 'تأكيد',
    billing_confirming: 'جارٍ التأكيد مع سترايب…',
    billing_confirming_lede: 'ثانية واحدة. تمت عملية الدفع بنجاح.',
    billing_thanks: 'شكرًا لك',
    billing_thanks_named: 'شكرًا لك يا {name}',
    billing_thanks_lede: 'مساحة عملك الآن على باقة {plan}. وهذا ما تم الاتفاق عليه.',
    billing_thanks_pending: 'دفعتك لدى سترايب. وسيظهر الاشتراك هنا خلال دقيقة.',
    billing_billing_starts: 'تبدأ المحاسبة',
    billing_topup_done: 'أُضيف الرصيد',
    billing_topup_done_lede: '{n} كريدت في حسابك. ولا تنتهي صلاحيتها.',
    company_settings: 'إعدادات الشركة',
    account_settings: 'حسابك',
    account_settings_lede:
      'اسمك وكلمة مرورك والشركات التي تنتمي إليها. هذه تتبعك أينما ذهبت — أما إعدادات الشركة فلا.',
    account_profile: 'الملف الشخصي',
    account_profile_lede: 'كيف تظهر لكل فريق تعمل معه.',
    account_title: 'المسمّى الوظيفي',
    account_email_fixed: 'عنوان تسجيل دخولك. تواصل مع الدعم لتغييره.',
    account_companies: 'شركاتك',
    account_companies_lede: 'حساب واحد، وأي عدد تريده من الشركات. ودورك قد يختلف في كل منها.',
    account_current: 'مفتوحة',
    account_open: 'انتقل إليها',
    account_security: 'الأمان',
    account_security_lede: 'كلمة مرورك تحمي كل شركة تنتمي إليها.',
    account_current_password: 'كلمة المرور الحالية',
    account_new_password: 'كلمة المرور الجديدة',
    account_change_password: 'تغيير كلمة المرور',
    account_password_scope:
      '8 أحرف على الأقل. تغييرها يسجّل خروجك من كل الأجهزة الأخرى.',
    account_password_changed: 'تم تغيير كلمة المرور.',
    account_password_changed_sessions: 'تم تغيير كلمة المرور. وأُنهيت {n} جلسة أخرى.',
    account_google_only:
      'هذا الحساب يسجّل الدخول عبر Google، فلا توجد كلمة مرور في وي هاي لتغييرها. أدِرها من حساب Google.',
    plan_trialing_on: 'قيد التجربة',
    plan_subscribe_to: 'اشترك',
    billing_leaving_checkout: 'جارٍ نقلك إلى صفحة الدفع الآمنة…',
    billing_leaving_topup: 'جارٍ نقلك إلى صفحة الدفع الآمنة…',
    billing_leaving_portal: 'جارٍ فتح بوابة الفوترة…',
    seat_limit_trial:
      'باقتك تشمل {included} مستخدم. اشترك لإضافة المزيد — كل مستخدم إضافي {price} شهريًا.',
    seat_limit_billing: 'فعّل الاشتراك قبل إضافة مستخدمين.',
    seat_next_costs: 'المستخدم التالي: {price} شهريًا',
    view_plans: 'عرض الباقات',
    billing_stripe_off: 'الاشتراكات غير مفعّلة في هذه النسخة.',
    billing_action_needed: 'الاشتراك يحتاج انتباهك',
    topup_title: 'أضف رصيد ذكاء اصطناعي',
    topup_subtitle: 'دفعة واحدة، والرصيد لا ينتهي بانتهاء الشهر.',
    topup_credits_word: 'رصيد',
    topup_addition:
      'هذه الـ{pack} تُضاف إلى {included} التي تتضمنها خطتك، فيصبح لديك {total} هذا الشهر.',
    topup_saving: 'أرخص بنسبة {percent}٪ لكل رصيد من أصغر باقة.',
    topup_cta: 'ادفع {price}',
    topup_leaving: 'نفتح صفحة الدفع…',
    topup_note: 'بيانات البطاقة تُدخل في Stripe، ولا نراها إطلاقًا.',
    credits_low_badge: 'الرصيد يقترب من النفاد',
    credits_out_badge: 'انتهى رصيد الذكاء الاصطناعي',
    credits_out_chat_title: 'انتهى رصيدك من الذكاء الاصطناعي',
    credits_out_chat_body: 'أضف رصيدًا لتكمل المحادثة. بقية النظام يعمل كما هو.',
    credits_out_chat_cta: 'أضف رصيدًا',
    trial_offer_badge: 'فعّل الاشتراك الآن: ١٢ يومًا إضافية مجانًا',
    trial_offer_short: '١٢ يومًا إضافية عند التفعيل',
    trial_last_day: 'آخر يوم في فترة التجربة',
    trial_ending_title: 'تنتهي فترة تجربتك خلال {n} أيام',
    trial_ending_body: 'فعّل الاشتراك الآن فتحصل على ١٢ يومًا مجانية أخرى قبل أول خصم، ثم ٥٥٪ خصمًا على الشهر الأول. لا شيء يتوقف اليوم.',
    trial_ending_cta: 'اختر باقة',
    hold_title: 'هذه المساحة موقوفة مؤقتًا',
    hold_body:
      'توقفت منذ انتهاء الاشتراك، ولم يُحذف شيء. اختر خطة لإعادتها، مع رسم لمرة واحدة قدره {fee} لاسترجاع البيانات وإعادة فتح الشركة.',
    hold_cta: 'استرجع الشركة',
    hold_note: 'دفاترك ومستنداتك وسجلّك كلها موجودة كما هي.',
    hold_warning:
      'إن بقي الاشتراك غير مدفوع، تتوقف المساحة في {date}. لا تُحذف: إعادة فتحها لاحقًا تكلّف خطة مع رسم استرجاع لمرة واحدة.',
    blocked_badge: 'للقراءة فقط',
    blocked_payment_title: 'توقفت مساحتك عن استقبال أي إدخال',
    blocked_payment_body:
      'لم تنجح دفعة الاشتراك، وانتهت مهلة إعادة المحاولة. تحديث البطاقة يعيد الكتابة فورًا.',
    blocked_payment_cta: 'حدّث طريقة الدفع',
    blocked_trial_title: 'انتهت فترة التجربة',
    blocked_trial_body:
      'اختر خطة لتعود إلى الإنشاء. كل ما أدخلته أثناء التجربة موجود كما هو.',
    blocked_trial_cta: 'اختر خطة',
    blocked_readonly_note:
      'لم يُحذف شيء. تستطيع الدخول وقراءة كل مستند وتصدير دفاترك.',
    blocked_keep_reading: 'أريد قراءة دفاتري',
    grace_banner_one: 'لم تنجح الدفعة. الكتابة تتوقف غدًا.',
    grace_banner_days: 'لم تنجح الدفعة. أمامك {days} أيام لتحديث البطاقة.',
    grace_banner_fix: 'عالج الأمر',
    billing_reason_trial_expired: 'انتهت التجربة المجانية. اشترك لمتابعة التعديلات.',
    billing_reason_payment_overdue: 'فشلت عملية الدفع وانتهت مهلة السماح.',
    billing_reason_payment_retrying: 'فشلت عملية الدفع — نعيد المحاولة.',
    billing_reason_subscription_inactive: 'الاشتراك غير فعّال.',
    billing_reason_organization_suspended: 'تم تعليق هذه المنشأة.',
    sub_status_trialing: 'تجربة',
    sub_status_active: 'فعّال',
    sub_status_past_due: 'متأخر السداد',
    sub_status_unpaid: 'غير مدفوع',
    sub_status_canceled: 'ملغى',
    sub_status_incomplete: 'غير مكتمل',
    sub_status_paused: 'موقوف',
    trial_days_left: 'باقي {n} يوم من التجربة',
    trial_billing_starts: 'تبدأ الفوترة في {date}',

    // الباقة المستنتجة من الاستخدام الفعلي للتجربة — services/planRecommendation.ts
    rec_title: 'الباقة التي يشير إليها استخدامك',
    rec_lede: 'محسوبة مما فعلته مساحة العمل فعلًا — لا من تخمين يوم التسجيل.',
    rec_days_left: 'باقي {n} يوم',
    rec_measured: 'مقيسة على مدى {days} يوم',
    rec_measured_seats: '{n} مستخدم في مساحة العمل',
    rec_measured_credits: 'استُهلك {used} كريدت ذكاء اصطناعي — أي نحو {projected} شهريًا بهذا المعدل',
    rec_measured_credits_zero: 'لم يُستهلك أي كريدت ذكاء اصطناعي',
    rec_answer: '{plan} مع {seats} مستخدم',
    rec_apply: 'اختر {plan}',
    rec_applied: 'تم ضبطها على {plan}. راجع الفاتورة ثم تابع متى شئت.',
    rec_choose_other:
      'اختر أي باقة أخرى بالأسفل، ولا يكلّفك تغيير رأيك شيئًا — هذه توصية لا بوابة.',
    rec_reason_used_ai: 'استخدمت الذكاء الاصطناعي، لذا باقة المبتدئ مستبعدة: لا كريدت فيها إطلاقًا.',
    rec_reason_too_early:
      'لم تستخدم الذكاء الاصطناعي بعد، لكن {days} يوم مدة أقصر من أن نعتبرها تفضيلًا — لذا أبقيناه مفعّلًا.',
    rec_reason_no_ai: 'لم يُستهلك أي كريدت ذكاء اصطناعي خلال {days} يوم.',
    rec_starter_tradeoff:
      'باقة المبتدئ تُسقِط المساعد ووكيل واتساب وكل ما يستهلك كريدت. أما حساباتك ومستنداتك وخادم MCP فتبقى كما هي.',
    rec_reason_used_whatsapp: 'واتساب متصل، لذا أُدرجت الإضافة في هذا العرض.',
    rec_reason_credit_burn: 'بمعدل نحو {projected} كريدت شهريًا، لن تكفي الباقة الأدنى إلى آخر الشهر.',
    rec_reason_credits_topped_up:
      'الباقة وحدها تمنح {included} كريدت شهريًا، لذا أُدرجت شريحة كريدت شهرية للوصول إلى {total}.',
    rec_reason_credits_short:
      'لا شيء نبيعه يغطي {projected} كريدت شهريًا. وهذا أكبر مخصّص متاح عند {total} — توقّع شراء باقة كريدت في الأشهر الكثيفة.',
    rec_reason_extra_seats: '{n} مستخدمين: {included} مشمول في الباقة، و{extra} إضافي بـ {price} لكل واحد.',
    rec_reason_seats_included: 'كل مستخدميك الـ{n} مشمولون في الباقة.',
    rec_expired_title: 'انتهت تجربتك المجانية',
    rec_expired_lede:
      'لم يُحذف شيء. كل ما أدخلته لا يزال هنا للقراءة والتصدير. أما الإدخالات الجديدة والذكاء الاصطناعي فتبقى متوقفة حتى تختار باقة.',

    // appearance
    theme: 'المظهر',
    theme_light: 'فاتح',
    theme_dark: 'داكن',
    theme_system: 'حسب النظام',
    addon_credits: '{n} رسالة شهريًا',
    addon_usage: 'استُخدمت {used} من {quota} رسالة هذا الشهر',
    enable: 'تفعيل',
    disable: 'إيقاف',
    on: 'مفعّل',
    off: 'متوقف',

    // organization
    org_products: 'المنتجات',
    org_products_hint: 'كل المنتجات مشمولة في الاشتراك. أوقف ما لا تحتاجه ليختفي من القائمة الجانبية.',
    org_members: 'الفريق',
    org_people_moved: 'تتم إدارة الفريق والأدوار من صفحة المستخدمين.',
    org_people_moved_action: 'فتح المستخدمين',
    org_seat_pricing_hint: 'المشاهدون مجانًا. كل دور آخر بـ 1٫5 د.ب شهريًا.',
    invite: 'دعوة',
    invite_sent: 'تم إرسال الدعوة',
    pending_invitations: 'الدعوات المعلّقة',
    no_pending_invitations: 'لا توجد دعوات معلّقة',
    revoke: 'إلغاء الدعوة',
    invite_person: 'دعوة شخص',
    expires: 'تنتهي في',
    seat: 'المقعد',
    billable: 'مدفوع',
    free: 'مجاني',

    // whatsapp
    whatsapp_upsell: 'مساعد واتساب يجيب عن أسئلة حساباتك، ويرسل تذكيرات الدفع وتقريرًا يوميًا — 4 د.ب شهريًا.',
    whatsapp_enable_addon: 'تفعيل الإضافة',
    whatsapp_number: 'رقم الأعمال',
    whatsapp_connect_hint: 'الصق بيانات الاعتماد من حساب واتساب للأعمال في ميتا.',
    whatsapp_connected: 'تم ربط واتساب',
    whatsapp_connected_as: 'مرتبط بالرقم {phone}',
    whatsapp_phone: 'رقم الهاتف (E.164)',
    whatsapp_phone_id: 'معرّف رقم الهاتف',
    whatsapp_token: 'رمز الوصول',
    whatsapp_contacts: 'جهات الاتصال المسموح لها',
    whatsapp_contacts_hint: 'لا يستطيع أي رقم قراءة بيانات الشركة قبل ربطه بمستخدم في وي هاي.',
    whatsapp_no_contacts: 'لم يراسل أحد الرقم بعد.',
    whatsapp_daily_report: 'التقرير اليومي',
    whatsapp_daily_report_hint: 'ملخص قصير لما تمّت فوترته وتحصيله وصرفه وما هو متأخر.',
    not_linked: 'غير مرتبط',
    connect: 'ربط',
    recipients: 'المستلمون',
    time: 'الوقت',
    last_sent: 'آخر إرسال',

    // ===== Billing cards =====
    ai_credits: 'أرصدة الذكاء الاصطناعي هذا الشهر',
    ai_credits_hint: 'تُحتسب من الاستهلاك الفعلي لنموذج {model} — نحو {n} محادثة مع المساعد.',
    feat_ai_credits: '{n} رصيد ذكاء اصطناعي شهريًا',
    billing_synced: 'تم تحديث الاشتراك من سترايب',
    sub_active: 'مُشترك',
    sub_confirmed: 'اشتراكك فعّال',
    sub_trial_then: 'مجاني حتى {date}، ثم يُحتسب شهريًا. يمكنك الإلغاء في أي وقت.',
    sub_card_on_file: 'يُحتسب شهريًا. يمكنك الإلغاء في أي وقت من بوابة الفوترة.',
    billing_ends_on: 'ينتهي في {date}',
    billing_already_paid: 'دفعت بالفعل؟ حدّث من سترايب',
    set_group_company: 'الشركة',
    set_group_workspace: 'مساحة العمل',
    set_group_billing: 'الاشتراك',
    set_group_integrations: 'التكاملات',
    set_desc_company: 'الاسم القانوني والسجل التجاري والرقم الضريبي والعنوان',
    set_desc_tax: 'نسبة الضريبة والعملة والسنة المالية',
    set_desc_branding: 'الشعار والألوان على مستنداتك',
    set_desc_org: 'ملف الشركة والمنتجات المستخدمة',
    set_desc_users: 'الفريق والأدوار والدعوات والمقاعد',
    set_desc_billing: 'الاشتراك والمقاعد والإضافات وأرصدة الذكاء الاصطناعي',
    set_desc_mcp: 'اربط كلود أو ChatGPT بحساباتك',
    set_desc_whatsapp: 'رقم الأعمال والتقارير اليومية',
    // ===== تعدد الشركات =====
    addon_mcp: 'الذكاء الاصطناعي وMCP',
    addon_mcp_d: 'اربط كلود أو ChatGPT بمساحة العمل هذه.',
    your_companies: 'شركاتك',
    new_company: 'شركة جديدة',
    create_company: 'إنشاء الشركة',
    new_company_hint: 'مساحة عمل منفصلة ببياناتها وفريقها واشتراكها. ستكون مالكها.',
    new_company_trial: 'تبدأ بتجربة مجانية 14 يومًا. بدون بطاقة.',
    org_created: '{name} جاهزة',
    country: 'الدولة',
    country_bh: 'البحرين',
    country_sa: 'السعودية',
    country_ae: 'الإمارات',
    country_kw: 'الكويت',
    country_qa: 'قطر',
    country_om: 'عُمان',
    seats_line_note: '{n} مستخدم مدفوع · {price} لكل مستخدم بعد الأول',
    module_always_on: 'دائم التفعيل — بقية النظام مبني عليه',
    current_plan: 'الحالي',
    addon_email: 'البريد الإلكتروني',
    plan_sub: 'كل ما يقدّمه وي هاي، للشركة كاملة.',
    plan_includes_seats: 'يشمل {n} مستخدم',
    per_user_month: '/ مستخدم / شهريًا',
    seats_sub: 'كم يكلّف كل شخص بعد الأول.',
    seats_in_use: '{n} مستخدم مدفوع حاليًا',
    seats_included: 'مشمول في الاشتراك',
    seats_extra: 'مستخدمون إضافيون',
    seats_cost: 'يُضاف للفاتورة',
    manage_people: 'إدارة الفريق',
    owner_only: 'المالك وحده يستطيع تعديل الاشتراك.',
    included_in_plan: 'مشمول في الاشتراك',
    trial_no_card: 'لا توجد بطاقة مسجّلة بعد',
    email_unlimited: 'بدون حد للإرسال',
    addon_used: 'المستخدم هذا الشهر',
    addon_remaining: 'المتبقي',
    addon_sub_whatsapp: 'رقم واتساب خاص بك، يجيب من دفتر الحسابات.',
    addon_sub_email: 'الفواتير وعروض الأسعار والتذكيرات والتقارير، تُرسل من وي هاي.',
    feat_all_products: 'ERP والمالية والعملاء والموارد البشرية والمخزون ونقاط البيع والذكاء الاصطناعي والنماذج',
    feat_viewers_free: 'المشاهدون مجانًا — القراءة فقط بلا تكلفة',
    feat_mcp: 'خادم MCP لكلود وChatGPT',
    feat_email: 'إرسال بريد بلا حدود',
    feat_seat_prorated: 'يُضاف ويُحذف خلال الشهر بحساب تناسبي',
    feat_wa_digest: 'ملخص يومي أو أسبوعي في الوقت الذي تختاره',
    feat_wa_reminders: 'تذكيرات دفع تتابع الفاتورة',
    feat_wa_arabic: 'إجابات بالعربية أو الإنجليزية',
    feat_email_docs: 'مرتبطة بالمستند الذي تخصّه',
    feat_email_log: 'كل رسالة مسجّلة وقابلة لإعادة الإرسال',

    /* ───────────────────────────────────────────────────────────────────────
       Phase 2B · People & partners — customers, vendors, employees, payroll,
       subscriptions, contracts. Append-only block; keep en/ar in sync.
       ─────────────────────────────────────────────────────────────────────── */
    customers_subtitle: 'الشركات والأشخاص الذين تصدر لهم فواتير.',
    vendors_subtitle: 'المورّدون الذين تشتري منهم وتدفع لهم.',
    employees_subtitle: 'فريقك ورواتبهم وعقودهم.',
    payroll_subtitle: 'دورات الرواتب وقسائمها ومدفوعاتها.',
    subscriptions_subtitle: 'الخدمات المتكررة وتكلفتها عليك.',
    contracts_subtitle: 'الاتفاقيات والتواقيع ومواعيد التجديد.',
    all_kinds: 'كل الأنواع',
    all_years: 'كل السنوات',
    clear_filters: 'مسح الفلاتر',
    no_results: 'لا شيء يطابق هذه الفلاتر',
    no_results_hint: 'جرّب بحثًا مختلفًا أو امسح الفلاتر.',
    no_customers_yet: 'لا يوجد عملاء بعد',
    no_customers_hint: 'أضف أول شركة تصدر لها فاتورة وستظهر هنا.',
    no_vendors_yet: 'لا يوجد موردون بعد',
    no_vendors_hint: 'أضف موردًا لتبدأ بتسجيل الفواتير والمصروفات عليه.',
    no_employees_yet: 'لا يوجد موظفون بعد',
    no_employees_hint: 'أضف أول عضو في الفريق لتشغيل الرواتب وإصدار العقود.',
    no_payroll_runs_yet: 'لا توجد دورات رواتب بعد',
    no_payroll_runs_hint: 'أنشئ دورة لتوليد قسائم الرواتب للفترة التي تختارها.',
    no_subscriptions_yet: 'لا توجد اشتراكات بعد',
    no_subscriptions_hint: 'تابع خدمة متكررة لترى تكلفتها وموعد الخصم القادم.',
    no_contracts_yet: 'لا توجد عقود بعد',
    no_contracts_hint: 'حرّر عقدًا ووقّعه إلكترونيًا واحتفظ بنسخة PDF هنا.',
    basic_info: 'الأساسيات',
    contact_details: 'بيانات التواصل',
    tax_registration: 'الضريبة والسجل',
    job_details: 'الوظيفة',
    compensation: 'الراتب',
    billing_schedule: 'جدول الخصم',
    posting_accounts: 'الترحيل',
    commercial_terms: 'القيمة والمدة',
    contract_body: 'نص العقد',
    esign_title: 'التوقيعات الإلكترونية',
    esign_subtitle: 'يوقّع الطرفان هنا، وتُطبع التواقيع في ملف PDF.',
    template_hint: 'ابدأ من قالب محفوظ ثم عدّل النص أدناه.',
    run_status_posted: 'مُرحّلة',
    run_stage_draft: 'لا تزال المبالغ قابلة للتعديل.',
    run_stage_posted: 'مُرحّلة إلى الدفاتر، بانتظار الصرف.',
    run_stage_paid: 'مصروفة من الحساب البنكي.',
    pending_edits_hint: 'أنهِ تعديل الاستقطاعات قبل الترحيل.',
    employee_count: 'الموظفون',
    back: 'رجوع',

    // ===== Phase 2A · صفحات المستندات (الفواتير، عروض الأسعار، فواتير الموردين، المصروفات، المدفوعات) =====
    line_items: 'البنود',
    more_actions: 'إجراءات أخرى',
    filter_search: 'بحث',
    invoices_sub: 'ما أصدرته من فواتير، وما لا يزال مستحقًا لك.',
    quotations_sub: 'العروض المرسلة للعملاء، وما آلت إليه.',
    bills_sub: 'ما أصدره الموردون عليك، وما لا يزال مستحقًا.',
    expenses_sub: 'المصروفات النقدية والبطاقات، من المطالبة حتى الترحيل.',
    payments_sub: 'كل حركة قبض وصرف، والمستند الذي سدّدته.',
    direction: 'الاتجاه',
    inbound: 'وارد',
    outbound: 'صادر',
    all_directions: 'كل الاتجاهات',
    inbound_payment: 'دفعة واردة',
    outbound_payment: 'دفعة صادرة',
    linked_to: 'مرتبطة بـ',
    no_linked_doc: 'لا يوجد مستند مرتبط',
    reference: 'المرجع',
    payslip: 'قسيمة راتب',
    created_at: 'أُنشئت',
    no_payments_sub: 'سدّد فاتورة أو فاتورة مورد أو دورة رواتب لتظهر هنا.',
    total_inbound: 'إجمالي الوارد',
    total_outbound: 'إجمالي الصادر',
    net_flow: 'الصافي',
    /* ───────────────────────────────────────────────────────────────────────
       المرحلة 2C · التقارير والمحاسبة والمكوّنات المشتركة. كتلة إضافية فقط؛
       أبقِ الإنجليزية والعربية متطابقتين.
       ─────────────────────────────────────────────────────────────────────── */
    stmt_total_revenue: 'إجمالي الإيرادات',
    stmt_total_expenses: 'إجمالي المصروفات',
    stmt_total_assets: 'إجمالي الأصول',
    stmt_total_liabilities: 'إجمالي الخصوم',
    stmt_total_equity: 'إجمالي حقوق الملكية',
    stmt_liabilities_and_equity: 'الخصوم وحقوق الملكية',
    stmt_net_margin: 'هامش الربح',
    stmt_net_movement: 'صافي الحركة',
    stmt_no_revenue: 'لا توجد إيرادات في هذه الفترة',
    stmt_no_expenses: 'لا توجد مصروفات في هذه الفترة',
    stmt_no_movement: 'لا توجد حركة في هذه الفترة',
    stmt_no_balances: 'لا توجد حسابات ذات رصيد',
    stmt_invoice_count: 'فاتورة',
    aging_by_bucket: 'المتبقي حسب العمر',
    aging_total_outstanding: 'إجمالي المتبقي',
    aging_nothing_outstanding: 'لا يوجد متبقٍّ',
    aging_all_settled: 'كل الفواتير المستحقة حتى هذا التاريخ مسدّدة.',
    days_overdue: 'يوم تأخير',
    coa_no_accounts: 'لا توجد حسابات من هذا النوع بعد',
    coa_expand: 'توسيع',
    coa_collapse: 'طي',
    journal_sub: 'كل قيد مُرحّل، ومصدره.',
    bank_accounts_sub: 'أين تُحفظ الأموال، ورصيد كل حساب.',
    upload: 'رفع',
    preview: 'معاينة',
    preview_unavailable: 'المعاينة غير متاحة لهذا النوع من الملفات.',
    close: 'إغلاق',
    aws_live_cost: 'تكلفة AWS حتى تاريخه',
    aws_not_configured: 'لم تُضبط مفاتيح فوترة AWS',
    edit_signature: 'تعديل التوقيع',

    /* ===== APPENDED — dashboard bands + post-checkout confirmation ===== */
    outstanding: 'المستحقات',
    performance: 'الأداء',
    activity: 'النشاط',
    checkout_confirming: 'جارٍ تأكيد اشتراكك…',
    checkout_confirming_sub: 'نستقبل التفاصيل من سترايب الآن، ولن يستغرق الأمر سوى لحظات.',
    checkout_done_title: 'أنت الآن على باقة {plan}',
    checkout_trial_line: 'مجاني حتى {date}، ثم {price} لكل {interval}.',
    checkout_renew_line: '{price} لكل {interval}. الخصم القادم في {date}.',
    checkout_charged_line: '{price} لكل {interval}، ابتداءً من اليوم.',
    checkout_first_month: 'شهرك الأول بـ {first}، ثم {price} شهريًا.',
    checkout_included: 'يشمل {seats} مستخدم · {credits} رصيد ذكاء اصطناعي شهريًا',
    checkout_cancel_note:
      'يمكنك الإلغاء في أي وقت من هذه الصفحة، وتبقى الخدمة فعّالة حتى نهاية المدة المدفوعة.',
    checkout_ack: 'تمام',
    checkout_pending_title: 'تم استلام الدفعة',
    checkout_pending_body:
      'وصلت دفعتك إلى سترايب. ستتحوّل باقتك خلال دقيقة — أعد تحميل الصفحة إن لم يحدث ذلك.',
    interval_month: 'شهر',
    interval_year: 'سنة',

    /* ===== APPENDED — تصميم المستندات (الإعدادات ← الهوية) =====
       كل ما يمكن للمنشأة تغييره في شكل ملفات PDF. كتلة إضافية فقط؛
       أبقِها مطابقة تمامًا للكتلة الإنجليزية أعلاه. */
    doc_design: 'تصميم المستندات',
    doc_design_sub: 'شكل ملفات PDF التي يستلمها عملاؤك. تُطبَّق التغييرات على كل مستند من الآن فصاعدًا.',
    doc_brand: 'الهوية',
    doc_logo: 'الشعار',
    doc_logo_hint: 'PNG أو JPEG أو WebP أو SVG. حتى 2 ميغابايت. يُطبع بالارتفاع الذي تختاره دون تمديد.',
    doc_logo_upload: 'رفع الشعار',
    doc_logo_replace: 'استبدال',
    doc_logo_remove: 'إزالة',
    doc_logo_none: 'لا يوجد شعار بعد — تستخدم المستندات اسم شركتك كعلامة نصية.',
    doc_logo_size: 'ارتفاع الطباعة',
    doc_logo_uploaded: 'تم تحديث الشعار',
    doc_logo_removed: 'تمت إزالة الشعار',
    doc_accent: 'اللون المميز',
    doc_accent_hint: 'يُستخدم لرقم المستند والإجماليات والخطوط.',
    doc_accent_inherit: 'استخدام لون هوية مساحة العمل',
    doc_header_style: 'شكل الترويسة',
    doc_header_rule: 'خط باللون المميز',
    doc_header_fill: 'شريط ملوّن',
    doc_header_fill_hint: 'الترويسة الملوّنة تحتاج شعارًا يظهر بوضوح فوق اللون.',
    doc_layout: 'التخطيط',
    doc_layout_classic: 'كلاسيكي',
    doc_layout_classic_desc: 'خطوط رفيعة وبطاقات محدّدة وتذييل داكن.',
    doc_layout_modern: 'حديث',
    doc_layout_modern_desc: 'اللون المميز يقود الصفحة. بطاقات ناعمة بلا حدود.',
    doc_layout_minimal: 'مبسّط',
    doc_layout_minimal_desc: 'بلا تعبئة. خطوط رفيعة وحجم صغير ومساحات واسعة.',
    doc_page: 'الصفحة',
    doc_language: 'لغة المستند',
    doc_language_en: 'الإنجليزية',
    doc_language_ar: 'العربية',
    doc_language_both: 'الاثنتان معًا',
    doc_language_hint: 'المستندات ثنائية اللغة تُبقي كل الأرقام من اليسار إلى اليمين، فلا تتحرك أعمدة المبالغ.',
    doc_paper: 'مقاس الورق',
    doc_paper_a4: 'A4',
    doc_paper_letter: 'Letter',
    doc_margin: 'الهوامش',
    doc_margin_hint: 'المسافة البيضاء حول الصفحة بالمليمتر.',
    doc_contents: 'ما يظهر على المستند',
    doc_show_cr: 'رقم السجل التجاري',
    doc_show_vat: 'الرقم الضريبي',
    doc_show_address: 'العنوان',
    doc_show_phone: 'الهاتف',
    doc_show_email: 'البريد الإلكتروني',
    doc_show_website: 'الموقع الإلكتروني',
    doc_website: 'عنوان الموقع',
    doc_show_iban: 'بيانات البنك والآيبان',
    doc_show_payment_terms: 'الشروط والملاحظات',
    doc_show_signature: 'سطر التوقيع',
    doc_show_watermark: 'ختم مسودة / مدفوعة',
    doc_show_watermark_hint: 'يضع ختمًا مائلًا على كل صفحة من المستندات غير النهائية والمسدّدة.',
    doc_text: 'النصوص',
    doc_terms_en: 'الشروط (بالإنجليزية)',
    doc_terms_ar: 'الشروط (بالعربية)',
    doc_terms_hint: 'تُطبع في خانة الاعتماد. اتركها فارغة لاستخدام الصيغة الافتراضية.',
    doc_footer_note_en: 'ملاحظة التذييل (بالإنجليزية)',
    doc_footer_note_ar: 'ملاحظة التذييل (بالعربية)',
    doc_footer_note_hint: 'سطر قصير واحد يتكرر أسفل كل صفحة.',
    doc_preview: 'معاينة حيّة',
    doc_preview_sub: 'القالب الحقيقي ببيانات تجريبية.',
    doc_preview_doc: 'مستند المعاينة',
    doc_preview_failed: 'تعذّر إنشاء المعاينة.',
    doc_preview_retry: 'إعادة المحاولة',
    doc_preview_open_pdf: 'فتح كملف PDF',
    doc_reset: 'استعادة الإعدادات الافتراضية',
    doc_reset_confirm: 'استعادة تصميم المستندات؟',
    doc_reset_body: 'ستعود كل إعدادات هذه الصفحة إلى الوضع الافتراضي، وسيُحذف الشعار المرفوع. المستندات المُرسلة سابقًا لا تتأثر.',
    doc_reset_done: 'تمت استعادة تصميم المستندات',
    doc_unsaved: 'تغييرات غير محفوظة',
    doc_invoice: 'فاتورة',
    doc_quote: 'عرض سعر',
    doc_bill: 'فاتورة مورّد',
    doc_payslip: 'قسيمة راتب',

    /* ===== APPENDED — إعادة بناء صفحة الاشتراك: الباقات والإضافات والرصيد ===== */
    plans_choose: 'اختر باقتك',
    plans_sub: 'غيّر باقتك متى شئت — يحتسب سترايب الفرق بالتناسب.',
    plan_current: 'باقتك الحالية',
    plan_switch_to: 'التحويل إليها',
    plan_upgrade_to: 'ترقية',
    plan_start_trial: 'ابدأ التجربة المجانية',
    interval_monthly: 'شهري',
    interval_yearly: 'سنوي',
    interval_yearly_note: 'شهران مجانًا',
    plan_billed_yearly: '{price} تُدفع مرة واحدة سنويًا',
    plan_seats_included: 'يشمل {n} مستخدمين',
    plan_seat_extra: 'ثم {price} لكل مستخدم إضافي',
    plan_ai_credits_month: '{n} رصيد ذكاء اصطناعي شهريًا',
    plan_no_ai: 'بلا رصيد ذكاء اصطناعي — خادم MCP فقط',
    plan_feat_mcp: 'خادم MCP لكلود وChatGPT',
    plan_feat_whatsapp: 'إمكانية إضافة مساعد واتساب',
    plan_feat_priority: 'دعم ذو أولوية',
    plan_feat_api: 'مفاتيح API لبرامجك الخاصة',
    offer_headline: 'خصم {percent}٪ على شهرك الأول',
    offer_sub: 'الشهر الأول بـ {price} بدل {full}. تُسجَّل البطاقة عند الدفع، ثم يتجدد بـ {full}.',
    offer_trial_first: 'أو جرّبه مجانًا {days} أيام أولًا — بلا بطاقة وبلا أي خصم.',
    offer_first_month: 'الشهر الأول {price}',
    offer_badge: 'وفّر {percent}٪',
    addons_title: 'الإضافات',
    addons_sub: 'خدمات إضافية يمكنك تفعيلها وإيقافها في أي وقت، وتُحتسب مع الاشتراك.',
    addon_brings: 'تشمل {messages} رسالة و{credits} رصيد ذكاء اصطناعي شهريًا.',
    addon_locked_on_plan: 'غير متاحة في باقة {plan}',
    addon_upgrade_to_add: 'متاحة ابتداءً من باقة {plan}',
    addon_on: 'مُفعّلة',
    credits_title: 'رصيد الذكاء الاصطناعي',
    credits_allowance: 'الرصيد الشهري',
    credits_left: 'بقي {left} من {quota}',
    credits_spent_this_period: 'استُهلك {n} هذا الشهر',
    credits_bought_balance: '{n} رصيد مُشترى — لا تنتهي صلاحيته',
    credits_exchanges: '≈ {n} محادثة مع المساعد',
    credits_burn: 'بمعدّل هذا الشهر يُستهلك نحو {n} يوميًا.',
    credits_days_cover: 'يكفي نحو {n} يومًا إضافيًا.',
    credits_runs_out: 'بمعدّل هذا الشهر سينفد الرصيد قبل نهاية الشهر.',
    credits_tier_title: 'ارفع الرصيد الشهري',
    credits_tier_sub: 'إضافة متكرّرة: تتجدّد مع الاشتراك ويُعاد ضبط الرصيد كل شهر.',
    credits_tier_included: 'المشمول في باقة {plan}',
    credits_tier_total: '{n} شهريًا',
    credits_tier_choose: 'اختيار',
    credits_tier_chosen: 'مختارة',
    credits_topup: 'شراء رصيد لمرة واحدة',
    credits_topup_sub: 'دفعة واحدة. يصل فورًا، ولا تنتهي صلاحيته، ولا يُستهلك إلا بعد نفاد رصيد الشهر.',
    credits_pack_credits: '{n} رصيد',
    credits_buy: 'شراء',
    credits_no_ai_plan: 'هذه الباقة بلا رصيد ذكاء اصطناعي — المساعد وواتساب والوكلاء مُعطّلون.',
    credits_no_ai_cta: 'الترقية إلى {plan}',
    topup_done_title: 'أُضيف الرصيد',
    topup_done_body: 'أُضيف {n} رصيد ذكاء اصطناعي إلى حسابك. لا تنتهي صلاحيته، ولا يُستهلك إلا بعد نفاد رصيد الشهر.',
    topup_pending_body: 'وصلت دفعتك إلى سترايب. سيصل الرصيد خلال دقيقة — أعد تحميل الصفحة إن لم يصل.',
    plan_change_title: 'التحويل إلى باقة {plan}',
    plan_change_new_price: 'السعر الجديد',
    plan_change_due_now: 'المستحق اليوم',
    plan_change_prorated: 'يحتسب سترايب فرق ما تبقّى من هذه الفترة بالتناسب.',
    plan_change_from_next: 'ومن الفاتورة القادمة تدفع {price} لكل {interval}.',
    plan_change_drops_addon: 'إضافة {addon} غير متاحة في هذه الباقة وسيتم إيقافها.',
    plan_change_confirm: 'تأكيد التحويل',
    plan_change_done: 'أنت الآن على باقة {plan}',
    billing_cancels_on: 'يُلغى في {date}',
    billing_what_you_pay: 'ما تدفعه',

    // ===== inventory: navigation and shells =====
    inventory: 'المخزون',
    products: 'المنتجات',
    products_sub: 'كل ما تبيعه من سلع وخدمات.',
    stock_levels: 'أرصدة المخزون',
    stock_levels_sub: 'ما لديك، وأين هو.',
    stock_movements: 'حركات المخزون',
    stock_movements_sub: 'كل وارد وصادر بالترتيب. لا يمكن تعديل هذا السجل.',
    stock_adjustments: 'تسويات المخزون',
    stock_adjustments_sub: 'الطريقة الوحيدة لتصحيح المخزون يدويًا، وكل عملية تترك سجلًا.',
    warehouses: 'المستودعات',
    warehouses_sub: 'أين يوجد المخزون فعليًا.',
    warehouse: 'المستودع',
    all_warehouses: 'كل المستودعات',
    new_warehouse: 'مستودع جديد',
    edit_warehouse: 'تعديل المستودع',
    warehouse_code: 'الرمز',
    warehouse_address: 'العنوان',
    close_warehouse: 'إغلاق المستودع',
    reopen_warehouse: 'إعادة الفتح',

    // ===== product =====
    product: 'المنتج',
    new_product: 'منتج جديد',
    edit_product: 'تعديل المنتج',
    product_details: 'تفاصيل المنتج',
    basics: 'الأساسيات',
    pricing: 'التسعير',
    kind_good: 'سلعة',
    kind_service: 'خدمة',
    kind_goods: 'السلع',
    kind_services: 'الخدمات',
    kind_good_hint: 'يُجرد ويُقيَّم ضمن المخزون.',
    kind_service_hint: 'يُباع بالوقت أو بالمهمة. لا شيء يُجرد.',
    sku: 'رمز الصنف',
    sku_hint: 'اتركه فارغًا ليُنشأ تلقائيًا.',
    unit: 'الوحدة',
    sale_price: 'سعر البيع',
    cost_hint: 'تُستخدم لتقييم المخزون وتسجيل تكلفة المبيعات.',
    cost_locked_hint: 'تُحتسب الآن من فواتير الشراء، والمتوسط أدناه هو قيمة المخزون.',
    average_cost: 'متوسط التكلفة',
    margin: 'هامش الربح',
    base_currency_hint: 'الأسعار بعملة الشركة الأساسية.',
    track_stock: 'تتبّع مخزون هذا المنتج',
    track_stock_hint: 'أوقفه لما تبيعه دون جرد، مثل كتيّب مطبوع.',
    track_stock_needs_module: 'فعّل منتج المخزون لتتمكن من جرد هذا الصنف.',
    opening_stock: 'الرصيد الافتتاحي للمخزون',
    opening_stock_hint: 'يُنشئ تسوية مخزون واحدة بتاريخ اليوم.',
    opening_unit_cost: 'تكلفة الوحدة الافتتاحية',
    income_account: 'حساب الإيراد',
    cogs_account: 'حساب تكلفة المبيعات',
    inventory_account: 'حساب المخزون',
    sold_last_90: 'المبيعات خلال آخر 90 يومًا',
    not_sold_yet: 'لم يُبَع بعد',
    revenue_90: 'الإيراد',
    cost_90: 'التكلفة',
    duplicate_product: 'نسخ',
    archive: 'أرشفة',
    unarchive: 'إلغاء الأرشفة',
    archived: 'مؤرشف',
    show_archived: 'إظهار المؤرشفة',
    export_csv: 'تصدير CSV',
    change_category: 'تغيير الفئة',
    manage_categories: 'الفئات',
    new_category: 'فئة جديدة',
    no_categories: 'لا توجد فئات بعد',
    n_selected: 'محدد',
    select_row: 'تحديد الصف',
    select_all_page: 'تحديد كل ما في هذه الصفحة',
    no_products: 'لا توجد منتجات بعد',
    no_products_sub:
      'أضِف السلع والخدمات التي تبيعها. يمكنك تسعيرها الآن وجرد الكميات لاحقًا.',
    no_products_match: 'لا توجد منتجات مطابقة',
    no_products_match_sub: 'جرّب بحثًا آخر أو أزِل عوامل التصفية.',
    product_search_ph: 'الاسم أو رمز الصنف',
    confirm_archive_products: 'أرشفة المنتجات المحددة؟ ستبقى كما هي في كل مستند ظهرت فيه.',
    confirm_delete_product: 'حذف هذا المنتج؟ لا يمكن حذفه إلا إذا لم يُستخدم في أي مستند.',

    // ===== product image =====
    image: 'الصورة',
    add_image: 'إضافة صورة',
    change_image: 'تغيير',
    remove_image: 'إزالة',
    image_drop_hint: 'أفلِت أو الصق أو انقر',
    image_removed: 'أُزيلت الصورة',
    image_pending: 'تُرفع عند الحفظ.',
    undo: 'تراجع',
    image_too_big: 'الصور فقط، بحد أقصى 5 ميغابايت.',

    // ===== stock =====
    stock: 'المخزون',
    on_hand: 'الكمية المتوفرة',
    committed: 'المحجوز',
    available: 'المتاح',
    inventory_value: 'قيمة المخزون',
    stock_value: 'قيمة المخزون',
    units_on_hand: 'إجمالي الوحدات',
    products_tracked: 'المنتجات المتتبَّعة',
    across_all_units: 'عبر كل الوحدات',
    moving_average: 'المتوسط المتحرك',
    stock_by_warehouse: 'المخزون حسب المستودع',
    negative_rows: 'أرصدة بالسالب',
    negative_stock_hint: 'بيع أكثر مما استُلم',
    no_stock_yet: 'لا يوجد مخزون مسجل بعد',
    no_stock_counted: 'لم يُجرد أي مخزون بعد',
    no_stock_counted_sub: 'اجرد مخزونك مرة واحدة وستُتتبع كل حركة بعدها تلقائيًا.',
    no_warehouses: 'لا توجد مستودعات بعد',
    no_warehouses_sub: 'أضِف مستودعًا قبل جرد المخزون.',
    add_warehouse: 'إضافة مستودع',
    stock_in_stock: 'متوفر',
    stock_low: 'منخفض',
    stock_out: 'نفد',
    stock_not_tracked: 'غير متتبَّع',
    below_reorder_only: 'تحت حد الطلب فقط',

    // ===== movements =====
    movement_change: 'التغيّر',
    movement_balance: 'الرصيد',
    movement_details: 'الحركة',
    recent_movements: 'أحدث الحركات',
    no_movements: 'لا توجد حركات بعد',
    no_movements_period: 'لا توجد حركات في هذه الفترة',
    no_movements_period_sub: 'جرّب فترة أوسع.',
    no_movements_ever: 'لا توجد حركات مخزون بعد',
    no_movements_ever_sub: 'تظهر الحركات هنا عند البيع والاستلام والجرد.',
    filtered_to_product: 'منتج واحد فقط',
    clear_filter: 'إزالة',
    mv_purchase: 'شراء',
    mv_sale: 'بيع',
    mv_adjustment: 'جرد',
    mv_write_off: 'إتلاف',
    mv_opening: 'رصيد افتتاحي',
    mv_transfer_in: 'تحويل وارد',
    mv_transfer_out: 'تحويل صادر',
    mv_reversal: 'عكس قيد',

    // ===== adjustments, the count flow =====
    adjustments: 'التسويات',
    stock_adjustment: 'تسوية مخزون',
    count_stock: 'جرد المخزون',
    new_adjustment: 'تسوية جديدة',
    edit_adjustment: 'تعديل التسوية',
    adjustment_number: 'رقم التسوية',
    adjustment_date: 'تاريخ التسوية',
    counted_by: 'سجّلها',
    warehouse_locked_hint: 'يُقفل بمجرد بدء الجرد.',
    system_qty: 'حسب النظام',
    counted_qty: 'المجرود',
    unit_cost: 'تكلفة الوحدة',
    line_value: 'القيمة',
    not_counted: 'لم يُجرد',
    n_counted: 'مجرود',
    filter_the_sheet: 'ابحث في كشف الجرد…',
    review_counts: 'مراجعة',
    post_count: 'ترحيل',
    post_count_title: 'ترحيل تسوية المخزون',
    post_count_warning: 'أنت على وشك تغيير المخزون. لا يمكن التراجع.',
    products_change: 'منتجات ستتغير',
    products_no_change: 'منتجات مطابقة ولن تتحرك.',
    net_value_change: 'الصافي',
    value_change: 'تغيّر القيمة',
    products_counted: 'المنتجات المجرودة',
    products_changed: 'المنتجات المتغيرة',
    count_reason: 'السبب',
    reason_count: 'تصحيح جرد',
    reason_write_off: 'تالف أو مفقود أو منتهي الصلاحية',
    reason_opening: 'رصيد افتتاحي',
    reason_count_hint: 'اكتب ما جردته فعليًا، ونحن نحسب الفرق.',
    reason_write_off_hint: 'اكتب الفاقد برقم سالب. الإتلاف لا يزيد المخزون أبدًا.',
    reason_opening_hint: 'مخزون كان موجودًا قبل استخدام Railit. يُسجَّل رأس مال للمالك لا إيرادًا.',
    count_attest: 'أُقر بأنني جردت هذا المخزون بنفسي.',
    status_posted: 'مرحّل',
    adjust_stock: 'تسوية المخزون',
    nothing_to_count: 'لا يوجد ما يُجرد في هذا المستودع',
    nothing_to_count_sub: 'أضِف منتجات أولًا أو اختر مستودعًا آخر.',
    no_adjustments: 'لا توجد تسويات مخزون بعد',
    no_adjustments_sub:
      'التسوية هي الطريقة الوحيدة لتصحيح المخزون يدويًا، وكل عملية تترك سجلًا.',
    no_adjustment_lines: 'لم يُجرد شيء بعد',
    confirm_post_adjustment:
      'ترحيل هذه التسوية؟ ستُسجَّل حركات المخزون والقيد المحاسبي، ولا يمكن التراجع.',
    confirm_void_adjustment: 'إلغاء هذه التسوية؟ تُعكس الحركات والقيد معًا.',
    confirm_delete_adjustment: 'حذف هذه المسودة؟ لم يُرحّل منها شيء بعد.',
    pick_warehouse_first: 'اختر مستودعًا ليظهر كشف الجرد.',

    // ===== low stock =====
    low_stock: 'مخزون منخفض',
    out_of_stock: 'نفد المخزون',
    below_reorder: 'تحت حد الطلب',
    reorder_point: 'حد إعادة الطلب',
    reorder_point_hint: 'ننبّهك عند النزول تحت هذا الحد.',
    reorder_qty: 'كمية إعادة الطلب',
    suggested_order: 'الطلب المقترح',
    low_stock_band: 'منتجات عند حد إعادة الطلب أو تحته.',
    out_of_stock_band: 'منتجات نفد مخزونها.',
    show_them: 'اعرضها',
    dismiss: 'إخفاء',

    // ===== units, the closed list from lib/units.ts =====
    unit_unit: 'وحدة',
    unit_piece: 'قطعة',
    unit_kg: 'كيلوغرام',
    unit_g: 'غرام',
    unit_litre: 'لتر',
    unit_ml: 'مليلتر',
    unit_metre: 'متر',
    unit_cm: 'سنتيمتر',
    unit_box: 'صندوق',
    unit_pack: 'عبوة',
    unit_carton: 'كرتون',
    unit_hour: 'ساعة',
    unit_day: 'يوم',

    // ===== server error codes =====
    item_sku_taken: 'هذا الرمز مستخدم بالفعل.',
    item_type_required: 'حدّد إن كان هذا سلعة أم خدمة.',
    item_in_use: 'هذا المنتج مستخدم في مستندات ولا يمكن حذفه. يمكنك أرشفته.',
    item_has_stock: 'لا يزال لدى هذا المنتج مخزون.',
    insufficient_stock: 'الكمية المتوفرة لا تكفي.',
    module_off: 'منتج المخزون غير مفعّل لهذه الشركة.',
    category_in_use: 'لا تزال هناك منتجات تستخدم هذه الفئة.',
  },
};

export type TranslationKey = keyof typeof I18N['en'];

/**
 * API error codes that have a friendly translation. Anything not listed here
 * falls back to the message the server sent.
 */
export const API_ERROR_KEY: Record<string, string> = {
  INVOICE_DISCOUNT_EXCEEDS_SUBTOTAL: 'discount_exceeds_subtotal',
  INVOICE_ADVANCE_VALUE_REQUIRED: 'advance_value_required',
  INVOICE_ADVANCE_INVALID: 'advance_invalid',
  INVOICE_ADVANCE_PERCENT_INVALID: 'advance_percent_invalid',
  INVOICE_ADVANCE_EXCEEDS_NET: 'advance_exceeds_net',
  INVOICE_ADVANCE_TYPE_REQUIRED: 'advance_type_required',
  ITEM_SKU_TAKEN: 'item_sku_taken',
  ITEM_TYPE_REQUIRED: 'item_type_required',
  ITEM_IN_USE: 'item_in_use',
  ITEM_HAS_STOCK: 'item_has_stock',
  INSUFFICIENT_STOCK: 'insufficient_stock',
  CATEGORY_IN_USE: 'category_in_use',
  MODULE_OFF: 'module_off',
};

const STORAGE_KEY = 'jf_lang';

function readStoredLang(): Lang {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    if (v === 'ar' || v === 'en') return v;
  } catch {
    // ignore
  }
  return 'en';
}

export interface I18nState {
  lang: Lang;
  dir: Dir;
  setLang: (lang: Lang) => void;
  t: (key: string) => string;
}

export function useI18nState(): I18nState {
  const [lang, setLangState] = useState<Lang>(() => readStoredLang());

  useEffect(() => {
    const root = document.documentElement;
    root.lang = lang;
    root.dir = lang === 'ar' ? 'rtl' : 'ltr';
    try {
      localStorage.setItem(STORAGE_KEY, lang);
    } catch {
      // ignore
    }
  }, [lang]);

  const setLang = useCallback((l: Lang) => {
    setLangState(l);
  }, []);

  const t = useCallback(
    (key: string): string => {
      const dict = I18N[lang];
      const value: string | undefined = dict[key];
      return value ?? key;
    },
    [lang],
  );

  return {
    lang,
    dir: lang === 'ar' ? 'rtl' : 'ltr',
    setLang,
    t,
  };
}
